<template>
  <form @submit.prevent="onSubmit">
    <!-- ------------ -->
    <!--   DIALOGS    -->
    <!-- ------------ -->
    <warning-dialog ref="warningDialog"></warning-dialog>
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <inventory-dialog ref="inventoryDialog"></inventory-dialog>
    <rune-linked-values-dialog
      ref="runeLinkedValuesDialog"
    ></rune-linked-values-dialog>
    <!-- ------------ -->
    <!--   NAVBAR     -->
    <!-- ------------ -->
    <div>
      <CharacterSheetNavbar />
    </div>
    <!-- --------------- -->
    <!--   PAGE HEADER   -->
    <!-- --------------- -->
    <div>
      <PageHeader />
    </div>
    <div style="position: relative">
      <!-- ---------------------------------------------- -->
      <!-- OVERLAY: character adjustments view (applied)  -->
      <!-- ---------------------------------------------- -->
      <div
        class="bb-full-overlay"
        v-if="isAdjsViewOpen"
      >
        <CharacterAdjustmentsView
          :applied="true"
          :adjs="filteredAdjs"
          :adj_category="adj_category"
          :adj_type="adj_type"
          :adj_label="adj_label"
          :min_adj="min_adj"
          :max_adj="max_adj"
          :items="characterItems"
          @save-skill-adjs="handleCalcTotalsRebuildReasonAppliedAdjs"
          @close-skill-adjs="handleCloseAdjs"
        />
      </div>
      <!-- ----------------------------------------------- -->
      <!-- OVERLAY: character adjustments view (optional)  -->
      <!-- ----------------------------------------------- -->
      <div
        class="bb-full-overlay"
        v-if="isOptAdjsViewOpen"
      >
        <CharacterAdjustmentsView
          :applied="false"
          :adjs="filteredOptAdjs"
          :adj_category="adj_category"
          :adj_type="adj_type"
          :adj_label="adj_label"
          :min_adj="min_adj"
          :max_adj="max_adj"
          :items="characterItems"
          @save-skill-adjs="handleCalcCountRebuildReasonOptAdjs"
          @close-skill-adjs="handleCloseAdjs"
        />
      </div>
      <!-- ------------------------------------------------------------>
      <!-- MAIN CONTAINER -->
      <!-- -------------- -->
      <div style="overflow-x: hidden !important">
        <div
          v-if="isReady"
          class="row"
        >
          <!-- ------------------- -->
          <!-- LEFT COL (BUTTONS)  -->
          <!-- ------------------- -->
          <div class="col-md-1 mt-1 px-3">
            <CharacterSheetButtonsPanel
              :isNewCharacter="isNewCharacter"
              :isChanged="isChanged"
              :character_name="local_character.name"
              :character_idx="character_idx"
              :isDetails="isDetails"
              :toggleDetails="toggleDetails"
              :activeTab="activeTab"
              :itemsCanScrollLeft="itemsCanScrollLeft"
              :itemsCanScrollRight="itemsCanScrollRight"
              :itemsNextHorizontalDir="itemsNextHorizontalDir"
              :canScrollDown="canScrollDown"
              :canScrollUp="canScrollUp"
              @close-character-sheet="handleSheetClose"
              @save-character-sheet="handleCharacterSave"
              @delete-character-sheet="handleCharacterDelete"
              @toggle-table-details="handleToggleTableDetails"
              @scroll-table="handleScrollTable"
              @add-character-object="handleAddCharacterObject"
              @roll-dice="handleHiddenRoll"
              @export-character-sheet="handleSheetExport"
            />
          </div>
          <!-- ---------------------------------------------------------->
          <!--  RIGHT COL (FORMS) -->
          <!-- ------------------ -->
          <div class="col-xl-11">
            <div class="mt-3 mb-3">
              <!-- =========================================== -->
              <!--               T A B   L I S T               -->
              <!-- =========================================== -->
              <ul
                :class="{ 'bb-profile-only-tabs': showProfileOnly }"
                class="nav nav-pills nav-justified"
                id="pills-tab"
                role="tablist"
              >
                <!------------------->
                <!-- TAB (PROFILE) -->
                <!------------------->
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3 active"
                    id="pills-profile-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-profile"
                    type="button"
                    role="tab"
                    aria-controls="pills-profile"
                    aria-selected="true"
                  >
                    Profile
                  </button>
                </li>
                <!------------------>
                <!-- TAB (COMBAT) -->
                <!------------------>
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3"
                    id="pills-combat-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-combat"
                    type="button"
                    role="tab"
                    aria-controls="pills-combat"
                    aria-selected="true"
                  >
                    Combat
                  </button>
                </li>
                <!---------------------------->
                <!-- TAB (CHARACTERISTICS)  -->
                <!---------------------------->
                <li class="nav-item dropdown">
                  <a
                    class="nav-link dropdown-toggle me-3"
                    style="font-size: 1em !important"
                    data-bs-toggle="dropdown"
                    role="pill"
                    aria-expanded="false"
                    >Characteristics</a
                  >
                  <ul
                    class="dropdown-menu dropdown-menu-dark"
                    role="presentation"
                  >
                    <!----------------------------------->
                    <!--  TAB (CHARACTERISTIC-DETAILS) -->
                    <!----------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-characteristics-details"
                        data-bs-toggle="pill"
                        >Details</a
                      >
                    </li>
                    <!----------------------------------------------->
                    <!--  TAB (CHARACTERISTICS-SKILL-FORMULA-ADJS) -->
                    <!----------------------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-characteristics-skill-formula-adjs"
                        data-bs-toggle="pill"
                        >Skill Formula Adjs</a
                      >
                    </li>
                  </ul>
                </li>
                <!---------------------->
                <!-- TAB (ABILITIES)  -->
                <!---------------------->
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3"
                    id="pills-abilities-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-abilities"
                    type="button"
                    role="tab"
                    aria-controls="pills-abilities"
                    aria-selected="true"
                  >
                    Abilities
                  </button>
                </li>
                <!------------------->
                <!-- TAB (SKILLS)  -->
                <!------------------->
                <li class="nav-item dropdown">
                  <a
                    class="nav-link dropdown-toggle me-3"
                    style="font-size: 1em !important"
                    data-bs-toggle="dropdown"
                    role="pill"
                    aria-expanded="false"
                    >Skills</a
                  >
                  <ul
                    class="dropdown-menu dropdown-menu-dark"
                    role="presentation"
                  >
                    <!-------------------------->
                    <!--  TAB (SKILLS-COMMON) -->
                    <!-------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-common"
                        data-bs-toggle="pill"
                        >Common</a
                      >
                    </li>
                    <!---------------------------->
                    <!--  TAB (SKILLS-UNCOMMON) -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-uncommon"
                        data-bs-toggle="pill"
                        >Uncommon</a
                      >
                    </li>
                    <!------------------------------>
                    <!--  TAB (SKILLS-LANGUAGES)  -->
                    <!------------------------------>
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-languages"
                        data-bs-toggle="pill"
                        >Languages</a
                      >
                    </li>
                    <!-------------------------->
                    <!--  TAB (SKILLS-LORES)  -->
                    <!-------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-lores"
                        data-bs-toggle="pill"
                        >Lores</a
                      >
                    </li>
                    <!-------------------------------->
                    <!--  TAB (SKILLS-SPECIALISMS)  -->
                    <!-------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-specialisms"
                        data-bs-toggle="pill"
                        >Specialisms</a
                      >
                    </li>
                    <!--------------------------------->
                    <!--  TAB (SKILLS-MELEE-WEAPON)  -->
                    <!--------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-melee-weapons"
                        data-bs-toggle="pill"
                        >Melee Weapons</a
                      >
                    </li>
                    <!---------------------------------->
                    <!--  TAB (SKILLS-RANGED-WEAPON)  -->
                    <!---------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-ranged-weapons"
                        data-bs-toggle="pill"
                        >Ranged Weapons</a
                      >
                    </li>
                    <!------------------------------------>
                    <!--  TAB (SKILLS-CASTING)          -->
                    <!------------------------------------>
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-casting"
                        data-bs-toggle="pill"
                        >Casting</a
                      >
                    </li>
                    <!--------------------->
                    <!-- DIVIDER         -->
                    <!--------------------->
                    <div class="dropdown-divider"></div>
                    <!---------------------------->
                    <!--  TAB (SKILLS-ALL)      -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-skills-all"
                        data-bs-toggle="pill"
                        >All Skills</a
                      >
                    </li>
                  </ul>
                </li>
                <!------------------->
                <!-- TAB (ITEMS)   -->
                <!------------------->
                <li class="nav-item dropdown">
                  <a
                    class="nav-link dropdown-toggle me-3"
                    style="font-size: 1em !important"
                    data-bs-toggle="dropdown"
                    role="pill"
                    aria-expanded="false"
                    >Items</a
                  >
                  <ul
                    class="dropdown-menu dropdown-menu-dark"
                    role="presentation"
                  >
                    <!---------------------------->
                    <!--  TAB (ITEMS-EQUIPMENT) -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-equipment"
                        data-bs-toggle="pill"
                        >Equipment</a
                      >
                    </li>
                    <!----------------------------->
                    <!--  TAB (ITEMS-AMMUNITION) -->
                    <!----------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-ammunition"
                        data-bs-toggle="pill"
                        >Ammunition</a
                      >
                    </li>
                    <!-------------------------------->
                    <!--  TAB (ITEMS-MELEE-WEAPON)  -->
                    <!-------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-melee-weapon"
                        data-bs-toggle="pill"
                        >Melee Weapons</a
                      >
                    </li>
                    <!--------------------------------->
                    <!--  TAB (ITEMS-RANGED-WEAPON)  -->
                    <!--------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-ranged-weapon"
                        data-bs-toggle="pill"
                        >Ranged Weapons</a
                      >
                    </li>
                    <!-------------------------------->
                    <!--  TAB (ITEMS-ARMOUR)        -->
                    <!-------------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-armour"
                        data-bs-toggle="pill"
                        >Armour</a
                      >
                    </li>
                    <!--------------------------->
                    <!--  TAB (ITEMS-HEALING)  -->
                    <!--------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-healing"
                        data-bs-toggle="pill"
                        >Healing</a
                      >
                    </li>
                    <!-------------------------->
                    <!--  TAB (ITEMS-POISON)  -->
                    <!-------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-poison"
                        data-bs-toggle="pill"
                        >Poison</a
                      >
                    </li>
                    <!------------------------------>
                    <!--  TAB (ITEMS-SPECIALIST)  -->
                    <!------------------------------>
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-specialist"
                        data-bs-toggle="pill"
                        >Specialist</a
                      >
                    </li>
                    <!------------------------->
                    <!--  TAB (ITEMS-MONEY)  -->
                    <!------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-money"
                        data-bs-toggle="pill"
                        >Money</a
                      >
                    </li>
                    <!---------------------------->
                    <!--  TAB (ITEMS-ARTEFACT)  -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-artefact"
                        data-bs-toggle="pill"
                        >Artefacts</a
                      >
                    </li>
                    <!--------------------->
                    <!-- DIVIDER         -->
                    <!--------------------->
                    <div class="dropdown-divider"></div>
                    <!---------------------------->
                    <!--  TAB (ITEMS-ALL)       -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-all"
                        data-bs-toggle="pill"
                        >All Items</a
                      >
                    </li>
                    <!---------------------------->
                    <!--  TAB (ITEMS-INVENTORY) -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-items-inventory"
                        @click.prevent="openInventoryDialog"
                        >Inventory</a
                      >
                    </li>
                  </ul>
                </li>
                <!------------------->
                <!-- TAB (RUNES)   -->
                <!------------------->
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3"
                    id="pills-runes-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-runes"
                    type="button"
                    role="tab"
                    aria-controls="pills-runes"
                    aria-selected="false"
                  >
                    Runes
                  </button>
                </li>
                <!-------------------------->
                <!-- TAB (HIT LOCATIONS)  -->
                <!-------------------------->
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3"
                    id="pills-hit-locations-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-hit-locations"
                    type="button"
                    role="tab"
                    aria-controls="pills-hit-locations"
                    aria-selected="false"
                  >
                    Hit Locations
                  </button>
                </li>
                <!------------------->
                <!-- TAB (SPELLS)  -->
                <!------------------->
                <li class="nav-item dropdown">
                  <a
                    class="nav-link dropdown-toggle me-3"
                    style="font-size: 1em !important"
                    data-bs-toggle="dropdown"
                    role="button"
                    aria-expanded="false"
                    >Spells</a
                  >
                  <ul
                    class="dropdown-menu dropdown-menu-dark"
                    role="presentation"
                  >
                    <!-------------------------->
                    <!--  TAB (SPELLS-RUNE)   -->
                    <!-------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-spells-rune"
                        data-bs-toggle="pill"
                        >Rune</a
                      >
                    </li>
                    <!---------------------------->
                    <!--  TAB (SPELLS-SORCERY)   -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-spells-sorcery"
                        data-bs-toggle="pill"
                        >Sorcery</a
                      >
                    </li>
                    <!--------------------------->
                    <!--  TAB (SPELLS-DIVINE)  -->
                    <!--------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-spells-divine"
                        data-bs-toggle="pill"
                        >Divine</a
                      >
                    </li>
                    <!--------------------->
                    <!-- DIVIDER         -->
                    <!--------------------->
                    <div class="dropdown-divider"></div>
                    <!---------------------------->
                    <!--  TAB (SPELLS-ALL)      -->
                    <!---------------------------->
                    <li>
                      <a
                        class="dropdown-item"
                        href="#pills-spells-all"
                        data-bs-toggle="pill"
                        >All Spells</a
                      >
                    </li>
                  </ul>
                </li>
                <!-------------------->
                <!-- TAB (TRAITS)   -->
                <!-------------------->
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3"
                    id="pills-traits-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-traits"
                    type="button"
                    role="tab"
                    aria-controls="pills-traits"
                    aria-selected="false"
                  >
                    Traits
                  </button>
                </li>
                <!-------------------->
                <!-- TAB (COUNTS)   -->
                <!-------------------->
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3"
                    id="pills-counts-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-counts"
                    type="button"
                    role="tab"
                    aria-controls="pills-counts"
                    aria-selected="true"
                  >
                    Counts
                  </button>
                </li>
                <!---------------------------->
                <!-- TAB (CAMPAIGN NOTES)   -->
                <!---------------------------->
                <li
                  class="nav-item"
                  role="presentation"
                >
                  <button
                    class="nav-link me-3"
                    id="pills-campaign-notes-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-campaign-notes"
                    type="button"
                    role="tab"
                    aria-controls="pills-campaign-notes"
                    aria-selected="true"
                  >
                    Notes
                  </button>
                </li>
                <!------------------->
                <!--   END TABS    -->
                <!------------------->
              </ul>
              <!-- ---------- -->
              <!-- ERRORS    -->
              <!-- ---------- -->
              <div
                v-if="errorMsgs.length > 0"
                class="row mt-3 mb-2"
              >
                <div class="form-group">
                  <!-- ------------ -->
                  <!--    errors    -->
                  <!-- ------------ -->
                  <div
                    v-for="err in errorMsgs"
                    :key="err.id"
                    class="bb-error mx-3"
                  >
                    {{ err }}
                  </div>
                </div>
              </div>
              <!-- =========================================== -->
              <!--            T A B    C O N T E N T           -->
              <!-- =========================================== -->
              <div
                class="tab-content"
                id="pills-tabContent"
              >
                <!----------------------------->
                <!--  TAB CONTENT (PROFILE)  -->
                <!----------------------------->
                <div
                  class="tab-pane fade show active"
                  id="pills-profile"
                  role="tabpanel"
                  aria-labelledby="pills-profile-tab"
                >
                  <CharacterSheetProfile
                    :character="local_character"
                    :errorFields="errorFields"
                    :list-scope="listScope"
                    @update="handleUpdate"
                    @clear-error="clearErrorField"
                  />
                </div>
                <!---------------------------->
                <!--  TAB CONTENT (COMBAT)  -->
                <!---------------------------->
                <div
                  class="tab-pane fade show"
                  id="pills-combat"
                  role="tabpanel"
                  aria-labelledby="pills-combat-tab"
                >
                  <CharacterSheetCombat
                    :character="local_character"
                    :errorFields="errorFields"
                    :list-scope="listScope"
                    @update="handleUpdate"
                    @sync-combat-state="handleSyncCombatState"
                    @clear-error="clearErrorField"
                  />
                </div>
                <!--------------------------------------------->
                <!--  TAB CONTENT (CHARACTERISTICS-DETAILS)  -->
                <!--------------------------------------------->
                <div
                  class="tab-pane fade show"
                  id="pills-characteristics-details"
                  role="tabpanel"
                  aria-labelledby="pills-characteristics-details-tab"
                >
                  <CharacterSheetCharacteristics
                    :character="local_character"
                    :is-new-character="isNewCharacter"
                    :current-race="currentCharacterRace"
                    @update="handleUpdate"
                    @adjust="handleOpenAdjs"
                  />
                </div>
                <!-------------------------------------------------------->
                <!--  TAB CONTENT (CHARACTERISTICS-SKILL-FORMULA-ADJS)  -->
                <!-------------------------------------------------------->
                <div
                  class="tab-pane fade show bb-static-tab-pane"
                  id="pills-characteristics-skill-formula-adjs"
                  role="tabpanel"
                  aria-labelledby="pills-characteristics-skill-formula-adjs-tab"
                >
                  <CharacterSheetCharacteristicSkillAdjs
                    :character="local_character"
                    @update="handleUpdate"
                    @adjust="handleOpenAdjs"
                  />
                </div>
                <!------------------------------->
                <!--  TAB CONTENT (ABILITIES)  -->
                <!------------------------------->
                <div
                  class="tab-pane fade show bb-static-tab-pane"
                  id="pills-abilities"
                  role="tabpanel"
                  aria-labelledby="pills-abilities-tab"
                >
                  <CharacterSheetAbilities
                    :character="local_character"
                    :config="config"
                    :list-scope="listScope"
                    @adjust="handleOpenAdjs"
                    @update="handleUpdate"
                  />
                </div>
                <!------------------------------------>
                <!--   TAB CONTENT (SKILLS-COMMON)  -->
                <!------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-skills-common"
                  role="tabpanel"
                  aria-labelledby="pills-skills-common-tab"
                >
                  <div v-if="activeTab === 'skills-common'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Common"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!----------------------------------------->
                <!--   TAB CONTENT (SKILLS-UNCOMMON)     -->
                <!----------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-skills-uncommon"
                  role="tabpanel"
                  aria-labelledby="pills-skills-uncommon-tab"
                >
                  <div v-if="activeTab === 'skills-uncommon'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Uncommon"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!---------------------------------------->
                <!--   TAB CONTENT (SKILLS-LANGUAGES)   -->
                <!---------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-skills-languages"
                  role="tabpanel"
                  aria-labelledby="pills-skills-languages-tab"
                >
                  <div v-if="activeTab === 'skills-languages'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Language"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!----------------------------------->
                <!--   TAB CONTENT (SKILLS-LORES)   -->
                <!------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-skills-lores"
                  role="tabpanel"
                  aria-labelledby="pills-skills-lores-tab"
                >
                  <div v-if="activeTab === 'skills-lores'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Lore"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!------------------------------------------>
                <!--   TAB CONTENT (SKILLS-SPECIALISMS)   -->
                <!------------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-skills-specialisms"
                  role="tabpanel"
                  aria-labelledby="pills-skills-specialisms-tab"
                >
                  <div v-if="activeTab === 'skills-specialisms'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Specialism"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!------------------------------------------>
                <!--   TAB CONTENT (SKILLS-MELEE-WEAPON)  -->
                <!------------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-skills-melee-weapons"
                  role="tabpanel"
                  aria-labelledby="pills-skills-melee-weapons-tab"
                >
                  <div v-if="activeTab === 'skills-melee-weapons'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Melee"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!------------------------------------------->
                <!--   TAB CONTENT (SKILLS-RANGED-WEAPON)  -->
                <!------------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-skills-ranged-weapons"
                  role="tabpanel"
                  aria-labelledby="pills-skills-ranged-weapons-tab"
                >
                  <div v-if="activeTab === 'skills-ranged-weapons'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Ranged"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!------------------------------------------>
                <!--   TAB CONTENT (SKILLS-CASTING)       -->
                <!------------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-skills-casting"
                  role="tabpanel"
                  aria-labelledby="pills-skills-casting-tab"
                >
                  <div v-if="activeTab === 'skills-casting'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="Casting"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!---------------------------------->
                <!--   TAB CONTENT (SKILLS-ALL)   -->
                <!---------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-skills-all"
                  role="tabpanel"
                  aria-labelledby="pills-skills-all-tab"
                >
                  <div v-if="activeTab === 'skills-all'">
                    <CharacterSheetSkills
                      ref="SheetSkills"
                      :character="local_character"
                      skill_type="All"
                      :max_skill="max_skill"
                      :activeTab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @adjust-character-skill="handleOpenAdjs"
                      @optional-adjust-character-skill="handleOpenOptAdjs"
                      @update-character-skill="handleUpdate"
                      @add-character-skill="handleAddSkill"
                      @delete-character-skill="handleDeleteSkill"
                    />
                  </div>
                </div>
                <!--------------------------------------->
                <!--   TAB CONTENT (ITEMS-EQUIPMENT)   -->
                <!--------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-equipment"
                  role="tabpanel"
                  aria-labelledby="pills-items-equipment-tab"
                >
                  <div v-if="activeTab === 'items-equipment'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Equipment"
                      title="Equipment"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!-------------------------------------->
                <!--   TAB CONTENT (ITEMS-AMMUNITION)  -->
                <!-------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-ammunition"
                  role="tabpanel"
                  aria-labelledby="pills-items-ammunition-tab"
                >
                  <div v-if="activeTab === 'items-ammunition'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Ammunition"
                      title="Ammunition"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!---------------------------------------->
                <!--   TAB CONTENT (ITEMS-MELEE-WEAPON) -->
                <!---------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-melee-weapon"
                  role="tabpanel"
                  aria-labelledby="pills-items-melee-weapon-tab"
                >
                  <div v-if="activeTab === 'items-melee-weapon'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Melee Weapon"
                      title="Melee Weapons"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!----------------------------------------->
                <!--   TAB CONTENT (ITEMS-RANGED-WEAPON) -->
                <!----------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-ranged-weapon"
                  role="tabpanel"
                  aria-labelledby="pills-items-ranged-weapon-tab"
                >
                  <div v-if="activeTab === 'items-ranged-weapon'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Ranged Weapon"
                      title="Ranged Weapons"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!------------------------------------>
                <!--   TAB CONTENT (ITEMS-ARMOUR)   -->
                <!------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-items-armour"
                  role="tabpanel"
                  aria-labelledby="pills-items-armour-tab"
                >
                  <div v-if="activeTab === 'items-armour'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Armour"
                      title="Armour"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!--------------------------------------->
                <!--   TAB CONTENT (ITEMS-ARTEFACT)    -->
                <!--------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-artefact"
                  role="tabpanel"
                  aria-labelledby="pills-items-artefact-tab"
                >
                  <div v-if="activeTab === 'items-artefact'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Artefact"
                      title="Artefacts"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!------------------------------------->
                <!--   TAB CONTENT (ITEMS-HEALING)   -->
                <!------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-healing"
                  role="tabpanel"
                  aria-labelledby="pills-items-healing-tab"
                >
                  <div v-if="activeTab === 'items-healing'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Healing"
                      title="Healing"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!------------------------------------->
                <!--   TAB CONTENT (ITEMS-POISON)   -->
                <!------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-poison"
                  role="tabpanel"
                  aria-labelledby="pills-items-poison-tab"
                >
                  <div v-if="activeTab === 'items-poison'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Poison"
                      title="Poison"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!---------------------------------------->
                <!--   TAB CONTENT (ITEMS-SPECIALIST)   -->
                <!---------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-specialist"
                  role="tabpanel"
                  aria-labelledby="pills-items-specialist-tab"
                >
                  <div v-if="activeTab === 'items-specialist'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Specialist"
                      title="Specialist"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!----------------------------------->
                <!--   TAB CONTENT (ITEMS-MONEY)   -->
                <!----------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-money"
                  role="tabpanel"
                  aria-labelledby="pills-items-money-tab"
                >
                  <div v-if="activeTab === 'items-money'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="Money"
                      title="Money"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!--------------------------------->
                <!--   TAB CONTENT (ITEMS-ALL)   -->
                <!--------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-all"
                  role="tabpanel"
                  aria-labelledby="pills-items-all-tab"
                >
                  <div v-if="activeTab === 'items-all'">
                    <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="All"
                      title="All Items"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    />
                  </div>
                </div>
                <!--------------------------------------->
                <!--   TAB CONTENT (ITEMS-INVENTORY)   -->
                <!--------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-items-inventory"
                  role="tabpanel"
                  aria-labelledby="pills-items-inventory-tab"
                >
                  <div v-if="activeTab === 'items-inventory'">
                    <!-- <CharacterSheetItems
                      ref="SheetItems"
                      :character="local_character"
                      item_type="All"
                      title="All Items"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @update-character-item="handleUpdate"
                      @add-character-item="handleAddItem"
                      @delete-character-item="handleDeleteItem"
                      @refresh-vertical-scroll-state="
                        refreshVerticalScrollState
                      "
                    /> -->
                  </div>
                </div>
                <!---------------------------->
                <!--  TAB CONTENT (RUNES)   -->
                <!---------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-runes"
                  role="tabpanel"
                  aria-labelledby="pills-runes-tab"
                >
                  <CharacterSheetRunes
                    ref="SheetRunes"
                    :character="local_character"
                    :max_skill="max_skill"
                    skill_type="Rune"
                    :active_tab="activeTab"
                    :errorFields="errorFields"
                    :toggleDetails="toggleDetails"
                    @add-character-rune="handleAddRune"
                    @delete-character-spell="handleDeleteSpell"
                    @delete-character-rune="handleDeleteRune"
                    @adjust-character-rune="handleOpenAdjs"
                    @update-character-rune="handleUpdate"
                  />
                </div>
                <!------------------------------------>
                <!--   TAB CONTENT (HIT LOCATIONS)  -->
                <!------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-hit-locations"
                  role="tabpanel"
                  aria-labelledby="pills-hit-locations-tab"
                >
                  <CharacterSheetHitLocations :character="local_character" />
                </div>
                <!------------------------------------->
                <!--   TAB CONTENT (SPELLS-RUNE)   *** NO UPDATES - JUST ADD/DELETE ***  -->
                <!------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-spells-rune"
                  role="tabpanel"
                  aria-labelledby="pills-spells-rune-tab"
                >
                  <div v-if="activeTab === 'spells-rune'">
                    <CharacterSheetSpells
                      ref="SheetSpells"
                      :character="local_character"
                      spell_type="Rune"
                      title="Equipment"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @add-character-spell="handleAddSpell"
                      @delete-character-spell="handleDeleteSpell"
                    />
                  </div>
                </div>
                <!------------------------------------->
                <!--   TAB CONTENT (SPELLS-SORCERY)  -->
                <!------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-spells-sorcery"
                  role="tabpanel"
                  aria-labelledby="pills-spells-sorcery-tab"
                >
                  <div v-if="activeTab === 'spells-sorcery'">
                    <CharacterSheetSpells
                      ref="SheetSpells"
                      :character="local_character"
                      spell_type="Sorcery"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @add-character-spell="handleAddSpell"
                      @delete-character-spell="handleDeleteSpell"
                      @update-character-spell="handleUpdate"
                    />
                  </div>
                </div>
                <!------------------------------------->
                <!--   TAB CONTENT (SPELLS-DIVINE)    -->
                <!------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-spells-divine"
                  role="tabpanel"
                  aria-labelledby="pills-spells-divine-tab"
                >
                  <div v-if="activeTab === 'spells-divine'">
                    <CharacterSheetSpells
                      ref="SheetSpells"
                      :character="local_character"
                      spell_type="Divine"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @add-character-spell="handleAddSpell"
                      @delete-character-spell="handleDeleteSpell"
                      @update-character-spell="handleUpdate"
                    />
                  </div>
                </div>
                <!------------------------------------->
                <!--   TAB CONTENT (SPELLS-ALL)    -->
                <!------------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-spells-all"
                  role="tabpanel"
                  aria-labelledby="pills-spells-all-tab"
                >
                  <div v-if="activeTab === 'spells-all'">
                    <CharacterSheetSpells
                      ref="SheetSpells"
                      :character="local_character"
                      spell_type="All"
                      :active_tab="activeTab"
                      :errorFields="errorFields"
                      :toggleDetails="toggleDetails"
                      @add-character-spell="handleAddSpell"
                      @delete-character-spell="handleDeleteSpell"
                      @update-character-spell="handleUpdate"
                    />
                  </div>
                </div>
                <!-------------------------------->
                <!--   TAB CONTENT (TRAITS)     -->
                <!-------------------------------->
                <div
                  class="tab-pane fade"
                  id="pills-traits"
                  role="tabpanel"
                  aria-labelledby="pills-traits-tab"
                >
                  <CharacterSheetTraits :character="local_character" />
                </div>
                <!---------------------------->
                <!--  TAB CONTENT (COUNTS)  -->
                <!---------------------------->
                <div
                  class="tab-pane fade show"
                  id="pills-counts"
                  role="tabpanel"
                  aria-labelledby="pills-counts-tab"
                >
                  <CharacterSheetCounts
                    ref="SheetCounts"
                    :character="local_character"
                    @update-character-count="handleUpdate"
                    @add-character-count="handleAddCount"
                    @delete-character-count="handleDeleteCount"
                  />
                </div>
                <!------------------------------------>
                <!--  TAB CONTENT (CAMPAIGN NOTES)  -->
                <!------------------------------------>
                <div
                  class="tab-pane fade"
                  id="pills-campaign-notes"
                  role="tabpanel"
                  aria-labelledby="pills-campaign-notes-tab"
                >
                  <CharacterSheetCampaignNotes
                    ref="SheetCampaignNotes"
                    :character="local_character"
                    @update-campaign-note="handleUpdate"
                    @add-campaign-note="handleAddCampaignNote"
                    @delete-campaign-note="handleDeleteCampaignNote"
                  />
                </div>
                <!------------------->
                <!--  END CONTENT  -->
                <!------------------->
              </div>
            </div>
          </div>
        </div>
        <!-- <p style="padding: 1px;"></p> -->
      </div>
    </div>
    <div
      v-if="!isReady"
      class="bb-loader"
    ></div>
  </form>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { nextTick } from 'vue'
import { axios } from '@/common/api.service.js'

//=====================
// components
//------------
import CharacterSheetCharacteristicSkillAdjs from '@/components/CharacterSheetCharacteristicSkillAdjs.vue'
import CharacterSheetCharacteristics from '@/components/CharacterSheetCharacteristics.vue'
import CharacterSheetCampaignNotes from '@/components/CharacterSheetCampaignNotes.vue'
import CharacterSheetButtonsPanel from '@/components/CharacterSheetButtonsPanel.vue'
import CharacterSheetHitLocations from '@/components/CharacterSheetHitLocations.vue'

import CharacterSheetAbilities from '@/components/CharacterSheetAbilities.vue'
// import CharacterSheetTallies from '@/components/CharacterSheetTallies.vue'
import CharacterSheetProfile from '@/components/CharacterSheetProfile.vue'
import CharacterSheetCounts from '@/components/CharacterSheetCounts.vue'

import CharacterSheetNavbar from '@/components/CharacterSheetNavbar.vue'
import CharacterSheetSkills from '@/components/CharacterSheetSkills.vue'
import CharacterSheetTraits from '@/components/CharacterSheetTraits.vue'
import CharacterSheetSpells from '@/components/CharacterSheetSpells.vue'
import CharacterSheetCombat from '@/components/CharacterSheetCombat.vue'
import CharacterSheetRunes from '@/components/CharacterSheetRunes.vue'
import CharacterSheetItems from '@/components/CharacterSheetItems.vue'
import InventoryDialog from '@/components/InventoryDialog.vue'

import ConfirmDialog from '@/components/ConfirmDialog.vue'
import RuneLinkedValuesDialog from '@/components/RuneLinkedValuesDialog.vue'
import WarningDialog from '@/components/WarningDialog.vue'
import PageHeader from '@/components/PageHeader.vue'

//=====================
// views
//-------
import CharacterAdjustmentsView from '../views/CharacterAdjustmentsView.vue'

//=====================
// processes
//-----------
import processCalcTotalsRebuildReasonAppliedAdjsHarness from '../processes/processCalcTotalsRebuildReasonAppliedAdjsHarness'
import processCalcTotalsRebuildReasonAppliedAdjs from '../processes/processCalcTotalsRebuildReasonAppliedAdjs'
import processCalcCountRebuildReasonOptAdjs from '../processes/processCalcCountRebuildReasonOptAdjs'
import processResetTotalsReasonAdjsHarness from '../processes/processResetTotalsReasonAdjsHarness'
import processCharacteristicChange from '../processes/processCharacteristicChange'

import processRemoveEquippedArmour from '../processes/processRemoveEquippedArmour'
import processSaveOriginalValues from '../processes/processSaveOriginalValues'
import processDeleteCampaignNote from '../processes/processDeleteCampaignNote'
import processAddEquippedArmour from '../processes/processAddEquippedArmour'

import processApplyNewRuneAdjs from '../processes/processApplyNewRuneAdjs'
import processAddCampaignNote from '../processes/processAddCampaignNote'
import processRemoveRuneAdjs from '../processes/processRemoveRuneAdjs'

import processDeleteSpell from '../processes/processDeleteSpell'
import processDeleteSkill from '../processes/processDeleteSkill'
import processDeleteCount from '../processes/processDeleteCount'
import processDeleteRune from '../processes/processDeleteRune'
import processDeleteItem from '../processes/processDeleteItem'
import processSubmission from '../processes/processSubmission'

import processSkillAdjs from '../processes/processSkillAdjs'
import processAddSkill from '../processes/processAddSkill'
import processAddCount from '../processes/processAddCount'
import processAddSpell from '../processes/processAddSpell'
import processAddRune from '../processes/processAddRune'
import processAddItem from '../processes/processAddItem'
import processChange from '../processes/processChange'

//=====================
// helpers
//---------
import syncCharacterCombatStateFromArmourLocations from '../helpers/syncCharacterCombatStateFromArmourLocations'
import convertAddSorceryCastingSkillActions from '../helpers/convertAddSorceryCastingSkillActions'
import getEffectiveCharacteristicSkillAdjs from '../helpers/getEffectiveCharacteristicSkillAdjs'
import syncArmourDexCharacteristicSkillAdj from '../helpers/syncArmourDexCharacteristicSkillAdj'
import syncCharacterOverloadingAdjustments from '../helpers/syncCharacterOverloadingAdjustments'
import convertAddRunecastingSkillActions from '../helpers/convertAddRunecastingSkillActions'
import normalizeCharacteristicSkillAdjs from '../helpers/normalizeCharacteristicSkillAdjs'

import {
  getFavouriteSkillBucketOverflow,
  projectSkillsWithChangedFavourites,
} from '../helpers/favouriteSkillBuckets'

import syncCharacterFatigueAdjustments from '../helpers/syncCharacterFatigueAdjustments'
import getSpecificRuneIntegratedCount from '../helpers/getSpecificRuneIntegratedCount'
import getSubstringAfterFindString from '../helpers/getSubstringAfterFindString'
import getSpecificRuneCastingSkill from '../helpers/getSpecificRuneCastingSkill'
import convertRaceArmourLocations from '../helpers/convertRaceArmourLocations'
import trackArmourLocationChanges from '../helpers/trackArmourLocationChanges'
import getLinkedRuneSpellEntries from '../helpers/getLinkedRuneSpellEntries'

import getCharacteristicPrefixes from '../helpers/getCharacteristicPrefixes'
import hasCharacterSheetChanges from '../helpers/hasCharacterSheetChanges'
import validateCharacterProfile from '../helpers/validateCharacterProfile'
import calcCharacteristicValues from '../helpers/calcCharacteristicValues'

import { getFatigueDetailsForLevel } from '../helpers/getFatigueDetails'
import { getOverloadingDetails } from '../helpers/getOverloadingDetails'

import createSocketConnection from '../helpers/createSocketConnection'
import calcContainerContents from '../helpers/calcContainerContents'
import capitaliseFirstLetter from '../helpers/capitaliseFirstLetter'
import formatHiddenRollValue from '../helpers/formatHiddenRollValue'
import convertDefaultSkills from '../helpers/convertDefaultSkills'

import convertStarterCounts from '../helpers/convertStarterCounts'
import {
  findDependentCharacterSkills,
  getMissingParentSkillMessage,
  hasCharacterSkill,
} from '../helpers/skillDependencyUtils'

import loadSkillAdjsHarness from '../helpers/loadSkillAdjsHarness'
import convertStarterItems from '../helpers/convertStarterItems'
import loadCharacterValues from '../helpers/loadCharacterValues'
import getAdjCollectionKey from '../helpers/getAdjCollectionKey'
import setCharacterValues from '../helpers/setCharacterValues'
import getAutoAdjPayloads from '../helpers/getAutoAdjPayloads'

import setProfileChanges from '../helpers/setProfileChanges'
import stripArrayTempIds from '../helpers/stripArrayTempIds'
import showNotification from '../helpers/showNotification'

import calcIntegsTotal from '../helpers/calcIntegsTotal'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import callSheetMethod from '../helpers/callSheetMethod'
import calcLocationHPs from '../helpers/calcLocationHPs'

import isSheetDetails from '../helpers/isSheetDetails'
import getArrayObject from '../helpers/getArrayObject'
import calcAbilities from '../helpers/calcAbilities'
import createTempId from '../helpers/createTempId'
import isEmpty from '../helpers/isEmpty'
//=====================
// images
//--------
import defaultImage from '../assets/images/default-character-image.png'
import infoImage from '../assets/images/bbQuestionMark96.png'
//==========================================================
// MAIN EXPORT
//-------------
export default {
  name: 'CharacterSheetView',
  //========================================================
  // components
  //------------
  components: {
    ConfirmDialog,
    RuneLinkedValuesDialog,
    PageHeader,
    CharacterSheetNavbar,
    CharacterSheetButtonsPanel,
    CharacterSheetProfile,
    CharacterSheetCombat,
    CharacterSheetCharacteristics,
    CharacterSheetCharacteristicSkillAdjs,
    CharacterSheetCampaignNotes,
    CharacterSheetSkills,
    CharacterSheetSpells,
    CharacterSheetRunes,
    CharacterSheetItems,
    InventoryDialog,
    CharacterSheetTraits,
    CharacterSheetAbilities,
    CharacterSheetCounts,
    CharacterSheetHitLocations,
    CharacterAdjustmentsView,
    WarningDialog,
  },
  //========================================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: false,
      default: null,
    },
    character_idx: {
      type: Number,
      required: false,
      default: 0,
    },
    listScope: {
      type: String,
      required: false,
      default: 'campaign',
    },
  },
  //========================================================
  // watch
  //-------
  watch: {
    character: {
      handler: function (newCharacter) {
        if (!newCharacter) {
          return
        }

        const incomingCharacterKey =
          newCharacter.slug || newCharacter.id || null
        const currentCharacterKey =
          this.local_character?.slug || this.local_character?.id || null

        if (incomingCharacterKey === currentCharacterKey) {
          return
        }

        this.loadCharacter(newCharacter)
        this.setCurrentCharacter(this.local_character)

        if (this.isReady) {
          void this.syncHiddenRollSocketConnection()
        }
      },
    },
    isChanged: {
      handler: function (newVal) {
        console.log('WATCH isChanged...', newVal)
      },
      immediate: true,
    },
    // -----------
    // active tab
    // -----------
    activeTab: {
      handler: function (newVal) {
        // console.log('WATCH ACTIVE TAB...', newVal)
        //-------------------
        // set details mode
        //-------------------
        this.isDetails = isSheetDetails(newVal)
        //--------------------------------
        // refresh vertical scroll state
        //--------------------------------
        this.$nextTick(() => {
          setTimeout(() => {
            this.refreshVerticalScrollState()
          }, 350)
        })
      },
      immediate: true,
    },
    // ----------------
    // local character
    // ----------------
    // NB: using separate baseline character obj for change detection rather than
    // new/oldValue as dep on timing both values could be the same
    // ---------------------------------------------------------------------------
    local_character: {
      deep: true,
      handler: function () {
        // ----------------------------------------------
        // manage character change where change detected
        // ----------------------------------------------
        if (
          Array.isArray(this.pending_changes) &&
          this.pending_changes.length > 0
        ) {
          this.manageCharacterChange()
        }
      },
    },
    showProfileOnly(newVal) {
      if (newVal) {
        this.enforceProfileTab()
      }
    },
    'local_character.arena': function () {
      if (this.isReady) {
        void this.syncHiddenRollSocketConnection()
      }
    },
    'local_character.arena_name': function () {
      if (this.isReady) {
        void this.syncHiddenRollSocketConnection()
      }
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      defaultImage,
      //
      config: null,
      original_character: null,
      local_character: null,
      change: null,
      prev_change: null,
      pending_changes: [],
      //
      errorMsgs: [],
      errorFields: [],
      errorTabs: [],
      //
      original_chars: [],
      original_adjs: [],
      original_skills: [],
      original_runes: [],
      original_items: [],
      original_counts: [],
      original_spells: [],
      original_armour_locations: [],
      original_campaign_notes: [],
      //
      changed_chars: [],
      changed_adjs: [],
      changed_skills: [],
      changed_runes: [],
      changed_items: [],
      changed_counts: [],
      changed_spells: [],
      changed_armour_locations: [],
      changed_campaign_notes: [],
      //
      original_profile: {},
      changed_profile: {},
      //
      default_race: '',
      // original_name: '',
      adj_type: '',
      adj_category: '',
      //
      isNewCharacterLoad: false,
      isReady: false,
      isDetails: false,
      isAdjsViewOpen: false,
      isOptAdjsViewOpen: false,
      toggleDetails: false, // summary
      //
      activeTab: 'profile',
      itemsCanScrollLeft: false,
      itemsCanScrollRight: true,
      itemsNextHorizontalDir: 'right',
      itemsLastHorizontalDir: 'right',
      canScrollDown: false,
      canScrollUp: false,
      arenaSocketConnection: null,
      hiddenRollArenaSlug: null,
      hiddenRollSocketSyncSeq: 0,
      //
      max_skill: 0,
      min_adj: 0,
      max_adj: 0,
      isCharacterOverloaded: false,
      overloadingAdjsApplied: false,
      lastAvailableEnc: 0,
    }
  },
  //========================================================
  // computed
  //----------
  computed: {
    isNewCharacter() {
      let isnew = this.local_character && isEmpty(this.local_character.slug)
      // console.log('isNewCharacter = ' + isnew)
      return isnew
    },
    isChanged() {
      return hasCharacterSheetChanges({
        changes: {
          profile: this.changed_profile,
          chars: this.changed_chars,
          skills: this.changed_skills,
          runes: this.changed_runes,
          items: this.changed_items,
          counts: this.changed_counts,
          spells: this.changed_spells,
          armour_locations: this.changed_armour_locations,
          campaign_notes: this.changed_campaign_notes,
        },
        original_adjs: this.original_adjs,
        character: this.local_character,
      })
    },
    showProfileOnly() {
      return (
        !!this.local_character?.status &&
        this.local_character.status !== 'Alive'
      )
    },
    characterItems() {
      return this.local_character ? this.local_character.items : []
    },
    currentCharacterRace() {
      if (!this.local_character) {
        return null
      }

      const getRace = this.$store.getters['races/getRaceByName']
      const allRaces = this.$store.getters['races/getRaces'] || []
      const characterRaces =
        this.$store.getters['races/getCharacterRaces'] || []
      const availableRaces =
        characterRaces.length > 0 ? characterRaces : allRaces
      const raceName = this.local_character.race_name
      const defaultHumanRace =
        availableRaces.find((entry) => entry?.name === 'Human') ||
        allRaces.find((entry) => entry?.name === 'Human') ||
        null

      return (
        (typeof getRace === 'function' && raceName
          ? getRace(raceName)
          : null) ||
        availableRaces.find((entry) => entry?.name === raceName) ||
        allRaces.find((entry) => entry?.name === raceName) ||
        defaultHumanRace ||
        availableRaces[0] ||
        allRaces[0] ||
        null
      )
    },
    //---------------------------------------------------------------------------------------------------
    filteredAdjs() {
      console.log(
        'IN filteredAdjs computed - category = ' +
          this.adj_category +
          ' type = ' +
          this.adj_type,
      )
      let filteredAdjs = []
      console.log('IN filteredAdjs character = ', this.local_character)
      //---------------------------------------------------------------------------
      // get category adjs for specific type (from respective tab) e.g. skill_adjs
      //---------------------------------------------------------------------------
      const adjs =
        this.adj_category === 'characteristic-skill'
          ? getEffectiveCharacteristicSkillAdjs(this.local_character)
          : this.local_character[getAdjCollectionKey(this.adj_category)]
      // console.log('adjs >>>>>>>>> ', adjs)
      if (!Array.isArray(adjs)) return []
      // --------------------------------------------------------------------
      // filter adjs by name = skill name e.g. type = "Athletics", "Stealth"
      // --------------------------------------------------------------------
      // NB: where type includes 'Rune' then map to 'Runecasting' adj name
      // NB: where category = 'skill' then only include applied = true
      //---------------------------------------------------------------------
      const name = this.adj_type.includes('Rune')
        ? 'Runecasting'
        : this.adj_type
      console.log('filter name >>>>>>>>> ', name)
      const isSkillCategory = this.adj_category === 'skill'

      filteredAdjs = adjs.filter((adj) => {
        const matchesName = adj.name === name || adj.name === this.adj_type
        if (!matchesName) return false
        // only enforce adj.applied === true for skill adjs
        if (!isSkillCategory) return true
        return adj.applied === true
      })
      console.log('filteredAdjs >>>>>>>>> ', filteredAdjs)
      return filteredAdjs
    },
    //---------------------------------------------------------------------------------------------------
    filteredOptAdjs() {
      console.log(
        'IN filteredOptAdjs computed - category = ' +
          this.adj_category +
          ' type = ' +
          this.adj_type,
      )
      let filteredOptAdjs = []
      console.log('IN filteredOptAdjs character = ', this.local_character)
      //---------------------------------------------------------------------------
      // get category adjs for specific type (from respective tab) e.g. skill_adjs
      //---------------------------------------------------------------------------
      const adjs =
        this.adj_category === 'characteristic-skill'
          ? getEffectiveCharacteristicSkillAdjs(this.local_character)
          : this.local_character[getAdjCollectionKey(this.adj_category)]
      console.log('adjs >>>>>>>>> ', adjs)
      if (!Array.isArray(adjs)) return []
      // --------------------------------------------------------------------
      // filter adjs by name = skill name e.g. type = "Athletics", "Stealth"
      // --------------------------------------------------------------------
      // NB: where type includes 'Rune' then map to 'Runecasting' adj name
      //---------------------------------------------------------------------
      const name = this.adj_type.includes('Rune')
        ? 'Runecasting'
        : this.adj_type
      console.log('filter name >>>>>>>>> ', name)
      const isSkillCategory = this.adj_category === 'skill'
      filteredOptAdjs = adjs.filter((adj) => {
        const matchesName = adj.name === name || adj.name === this.adj_type
        if (!matchesName) return false
        // only enforce adj.applied === false for skill adjs
        if (!isSkillCategory) return true
        return adj.applied === false
      })
      // }
      console.log('filteredOptAdjs >>>>>>>>> ', filteredOptAdjs)
      return filteredOptAdjs
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    async openInventoryDialog() {
      if (!this.local_character || !this.$refs.inventoryDialog) {
        return
      }

      await this.$refs.inventoryDialog.show({
        character: this.local_character,
      })
    },
    calculateCurrentEnc(items = []) {
      return (
        Math.round(
          (items.reduce((total, item) => {
            const encPerItem = parseFloat(item?.enc || 0)
            const qty = parseInt(item?.tally_qty || 0, 10)

            if (Number.isNaN(encPerItem) || Number.isNaN(qty)) {
              return total
            }

            return total + encPerItem * qty
          }, 0) +
            Number.EPSILON) *
            100,
        ) / 100
      )
    },
    calculateMaxEnc(character = this.local_character) {
      const str = Number(character?.str_cur ?? 0)
      const siz = Number(character?.siz_cur ?? 0)
      const safeStr = Number.isNaN(str) ? 0 : str
      const safeSiz = Number.isNaN(siz) ? 0 : siz
      return Math.round((safeStr + 2 * safeSiz + Number.EPSILON) * 100) / 100
    },
    refreshCharacterEncState() {
      if (!this.local_character) {
        return
      }

      const items = Array.isArray(this.local_character.items)
        ? [...this.local_character.items]
        : []
      const currentEnc = this.calculateCurrentEnc(items)

      this.local_character = {
        ...this.local_character,
        items,
        max_enc: this.calculateMaxEnc(this.local_character),
        cur_enc: currentEnc,
      }
    },
    calculateAvailableEnc(character = this.local_character, currentEnc = null) {
      const nextCurrentEnc =
        currentEnc === null
          ? this.calculateCurrentEnc(character?.items || [])
          : currentEnc

      return (
        Math.round(
          (this.calculateMaxEnc(character) - nextCurrentEnc + Number.EPSILON) *
            100,
        ) / 100
      )
    },
    formatSignedEnc(value) {
      const num = Number(value)
      const safeValue = Number.isNaN(num) ? 0 : num
      const rounded = Math.round((safeValue + Number.EPSILON) * 100) / 100
      const normalized = Object.is(rounded, -0) ? 0 : rounded
      const prefix = normalized > 0 ? '+' : ''
      return `${prefix}${normalized.toFixed(2)}`
    },
    hasAutoOverloadingAdjustmentsEnabled() {
      return this.config?.auto_apply_overloading_adjs === true
    },
    buildOverloadingMessage({
      isOverloaded,
      availableEnc,
      adjustmentsApplied,
      adjustmentsRemoved,
    }) {
      const lines = [
        isOverloaded
          ? 'Character Overloaded'
          : 'Character No Longer Overloaded',
        `(Enc ${this.formatSignedEnc(availableEnc)})`,
      ]

      if (adjustmentsApplied) {
        lines.push('Adjustments Applied')
      } else if (adjustmentsRemoved) {
        lines.push('Adjustments Removed')
      }

      return lines.join('\n')
    },
    async syncCharacterOverloadingState({
      showTransitionPopup = false,
      showInitialPopup = false,
    } = {}) {
      if (!this.local_character) {
        return {
          availableEnc: 0,
          currentEnc: 0,
          isOverloaded: false,
          adjustmentsApplied: false,
        }
      }

      const previousIsOverloaded = this.isCharacterOverloaded === true
      const previousAdjustmentsApplied = this.overloadingAdjsApplied === true

      const currentEnc = this.calculateCurrentEnc(
        this.local_character.items || [],
      )
      const availableEnc = this.calculateAvailableEnc(
        this.local_character,
        currentEnc,
      )
      const overloadingDetails = getOverloadingDetails(availableEnc)
      const autoApplyOverloadingAdjs =
        this.hasAutoOverloadingAdjustmentsEnabled()
      const appliedOverloadingDetails = autoApplyOverloadingAdjs
        ? overloadingDetails
        : { level: 'Not Overloaded', adj_move: 0, adj_skills: 0 }

      this.local_character.cur_enc = currentEnc
      this.local_character = syncCharacterOverloadingAdjustments(
        this.local_character,
        appliedOverloadingDetails,
      )
      this.local_character.cur_enc = currentEnc

      const isOverloaded = availableEnc < 0
      const adjustmentsApplied =
        autoApplyOverloadingAdjs &&
        isOverloaded &&
        ((Number(overloadingDetails?.adj_move) || 0) !== 0 ||
          (Number(overloadingDetails?.adj_skills) || 0) !== 0)

      const shouldShowPopup =
        (showInitialPopup && isOverloaded) ||
        (showTransitionPopup && previousIsOverloaded !== isOverloaded)

      if (shouldShowPopup) {
        await this.$refs.warningDialog.show({
          title: isOverloaded ? 'WARNING' : 'NOTE',
          message: this.buildOverloadingMessage({
            isOverloaded,
            availableEnc,
            adjustmentsApplied,
            adjustmentsRemoved:
              previousAdjustmentsApplied && !adjustmentsApplied,
          }),
          okButton: 'OK',
        })
      }

      this.isCharacterOverloaded = isOverloaded
      this.overloadingAdjsApplied = adjustmentsApplied
      this.lastAvailableEnc = availableEnc

      return {
        availableEnc,
        currentEnc,
        isOverloaded,
        adjustmentsApplied,
      }
    },
    //=============================================
    // mapped methods
    //----------------
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
    }),
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
      getUserProfile: 'getUserProfile',
    }),
    ...mapMutations('characters', {
      setCurrentCharacter: 'setCurrentCharacter',
      resetCurrentCharacter: 'resetCurrentCharacter',
    }),
    ...mapGetters('combat', {
      getConfig: 'getConfig',
      getCampaign: 'getCampaign',
    }),
    ...mapGetters('characters', {
      getDefaultSkills: 'getDefaultSkills',
      getStarterCounts: 'getStarterCounts',
      getStarterItems: 'getStarterItems',
      getStaticSkill: 'getStaticSkill',
    }),
    ...mapActions('combat', {
      loadCombatConfig: 'loadConfig',
    }),
    ...mapActions('characters', {
      loadStaticAbilities: 'loadStaticAbilities',
      loadStaticSkills: 'loadStaticSkills',
      loadStaticTraits: 'loadStaticTraits',
      loadStaticRunes: 'loadStaticRunes',
      loadStaticSpells: 'loadStaticSpells',
      loadStaticItems: 'loadStaticItems',
      loadStaticStarterCounts: 'loadStaticStarterCounts',
      loadItemLookups: 'loadItemLookups',
      loadArtefactTypes: 'loadArtefactTypes',
      loadCharacterLookups: 'loadCharacterLookups',
      loadStaticCurrencies: 'loadStaticCurrencies',
    }),
    showSkillNotification({ action = 'updated', skillName = '' } = {}) {
      const normalizedAction = action === 'added' ? 'added' : 'updated'
      const normalizedSkillName =
        typeof skillName === 'string' ? skillName.trim() : ''

      const msg = normalizedSkillName
        ? `Skill ${normalizedAction}: ${normalizedSkillName}.`
        : `Skill ${normalizedAction}.`

      showNotification(msg)
    },
    formatItemValueNumber(value) {
      const numericValue = Number(value)

      if (!Number.isFinite(numericValue)) {
        return ''
      }

      return Number.isInteger(numericValue)
        ? `${numericValue}`
        : numericValue.toFixed(2).replace(/\.?0+$/, '')
    },
    getItemCurrencySuffix(rawCurrency) {
      if (
        rawCurrency === null ||
        rawCurrency === undefined ||
        rawCurrency === ''
      ) {
        return ''
      }

      if (typeof rawCurrency === 'object') {
        return (
          rawCurrency.short_name || rawCurrency.name || rawCurrency.id || ''
        )
      }

      const currencies = this.$store.getters['characters/getCurrencies'] || []
      const matchedCurrency = Array.isArray(currencies)
        ? currencies.find(
            (currency) =>
              currency &&
              (currency.id === rawCurrency ||
                currency.short_name === rawCurrency ||
                currency.name === rawCurrency),
          )
        : null

      return matchedCurrency?.short_name || matchedCurrency?.name || rawCurrency
    },
    getHiddenRollPlayerName() {
      const userProfile = this.getUserProfile() || {}

      return (
        userProfile.first_name ||
        userProfile.user_name ||
        this.local_character?.player_name ||
        'Player'
      )
    },
    async showHiddenRollPopup(message = {}) {
      const currentUser = this.$store.getters['auth/getUserProfile'] || {}
      const currentUserIds = [currentUser?.id, currentUser?.email]
        .filter(
          (value) => value !== null && value !== undefined && value !== '',
        )
        .map((value) => String(value))

      if (!currentUserIds.includes(String(message?.dm_id))) {
        return false
      }

      await this.$refs.confirmDialog?.show?.({
        title: 'Hidden Roll to DM',
        message: `from ${message?.player_name || 'Player'}`,
        subject: formatHiddenRollValue(message?.roll),
        subjectReadonly: true,
        okButton: 'OK',
      })

      return true
    },
    isCurrentUserHiddenRollRecipient(message = {}) {
      const currentUser = this.$store.getters['auth/getUserProfile'] || {}
      const currentUserIds = [currentUser?.id, currentUser?.email]
        .filter(
          (value) => value !== null && value !== undefined && value !== '',
        )
        .map((value) => String(value))

      return currentUserIds.includes(String(message?.dm_id))
    },
    acknowledgeHiddenRollDelivery(connection, message = {}) {
      if (
        !message?.request_id ||
        !connection ||
        connection.readyState !== WebSocket.OPEN
      ) {
        return
      }

      connection.send(
        JSON.stringify({
          message: {
            type: 'hidden_roll_ack',
            request_id: message.request_id,
          },
        }),
      )
    },
    async resolveHiddenRollArenaSlug() {
      const currentCampaign = this.getCampaign()
      const localArenaId = this.local_character?.arena
      const localArenaName = this.local_character?.arena_name

      if (
        currentCampaign?.slug &&
        localArenaId != null &&
        String(currentCampaign.id) === String(localArenaId)
      ) {
        return currentCampaign.slug
      }

      const headers = this.getHeaders() || {}
      const endpoints = []

      if (this.listScope === 'mine') {
        endpoints.push('api/v1/campaigns/member/')
      }

      endpoints.push('api/v1/campaigns/my/')
      endpoints.push('api/v1/campaigns/list/')

      for (const initialEndpoint of endpoints) {
        let nextUrl = initialEndpoint

        while (nextUrl) {
          const response = await axios.get(nextUrl, {
            headers,
            mode: 'same-origin',
          })
          const results = Array.isArray(response?.data?.results)
            ? response.data.results
            : []
          const campaign = results.find(
            (entry) =>
              String(entry?.id) === String(localArenaId) ||
              String(entry?.name) === String(localArenaName),
          )

          if (campaign?.slug) {
            return campaign.slug
          }

          nextUrl = response?.data?.next ?? null
        }
      }

      return null
    },
    teardownArenaSocketConnection() {
      if (
        this.arenaSocketConnection &&
        (this.arenaSocketConnection.readyState === WebSocket.OPEN ||
          this.arenaSocketConnection.readyState === WebSocket.CONNECTING)
      ) {
        this.arenaSocketConnection.close(1000)
      }

      this.arenaSocketConnection = null
      this.hiddenRollArenaSlug = null
    },
    ensureArenaSocketConnection(arenaSlug) {
      if (!arenaSlug) {
        this.teardownArenaSocketConnection()
        return
      }

      const existingConnection = this.arenaSocketConnection
      if (
        existingConnection &&
        this.hiddenRollArenaSlug === arenaSlug &&
        (existingConnection.readyState === WebSocket.OPEN ||
          existingConnection.readyState === WebSocket.CONNECTING)
      ) {
        return
      }

      this.teardownArenaSocketConnection()

      const connection = createSocketConnection('arena', arenaSlug)
      connection.bbArenaSlug = arenaSlug
      connection.onmessage = (event) => {
        const data = JSON.parse(event.data || '{}')
        const message = data?.message

        if (message?.type === 'hidden_roll_delivery') {
          if (this.isCurrentUserHiddenRollRecipient(message)) {
            this.acknowledgeHiddenRollDelivery(connection, message)
          }
          void this.showHiddenRollPopup(message)
        }
      }

      this.arenaSocketConnection = connection
      this.hiddenRollArenaSlug = arenaSlug
    },
    async syncHiddenRollSocketConnection() {
      const syncSeq = ++this.hiddenRollSocketSyncSeq
      const arenaSlug = await this.resolveHiddenRollArenaSlug()

      if (syncSeq !== this.hiddenRollSocketSyncSeq) {
        return this.hiddenRollArenaSlug
      }

      const hasArenaAssignment =
        this.local_character?.arena != null ||
        !isEmpty(this.local_character?.arena_name)

      if (!arenaSlug && hasArenaAssignment) {
        return this.hiddenRollArenaSlug
      }

      this.ensureArenaSocketConnection(arenaSlug)
      return arenaSlug
    },
    sendHiddenRollRequest({ arenaSlug, playerName, roll }) {
      return new Promise((resolve) => {
        const connection = createSocketConnection('arena', arenaSlug, {
          logLifecycle: false,
        })
        let settled = false
        const timeoutId = window.setTimeout(() => {
          finish({ status: 'dm_unavailable' })
        }, 3000)

        const finish = (payload) => {
          if (settled) {
            return
          }

          settled = true
          window.clearTimeout(timeoutId)

          if (
            connection.readyState === WebSocket.OPEN ||
            connection.readyState === WebSocket.CONNECTING
          ) {
            connection.close(1000)
          }

          resolve(payload)
        }

        connection.addEventListener('open', () => {
          connection.send(
            JSON.stringify({
              message: {
                type: 'hidden_roll_request',
                player_name: playerName,
                roll,
              },
            }),
          )
        })

        connection.addEventListener('message', (event) => {
          const payload = JSON.parse(event.data || '{}')
          const message = payload?.message || {}

          console.log('hidden roll request socket message = ', message)

          if (message?.type === 'hidden_roll_result') {
            finish(message)
          }
        })

        connection.addEventListener('error', () => {
          finish({ status: 'dm_unavailable' })
        })

        connection.addEventListener('close', (event) => {
          if (settled || event.code === 1000) {
            return
          }

          finish({ status: 'dm_unavailable' })
        })
      })
    },
    async handleHiddenRoll() {
      const arenaSlug = await this.resolveHiddenRollArenaSlug()

      if (isEmpty(arenaSlug)) {
        await this.$refs.warningDialog.show({
          title: 'Hidden Roll',
          message: 'DM not available.',
          okButton: 'OK',
        })
        return
      }

      const formattedRoll = formatHiddenRollValue(
        Math.floor(Math.random() * 100) + 1,
      )
      const result = await this.sendHiddenRollRequest({
        arenaSlug,
        playerName: this.getHiddenRollPlayerName(),
        roll: formattedRoll,
      })

      if (result?.status === 'sent') {
        await this.$refs.warningDialog.show({
          title: 'Hidden Roll',
          message: 'Roll Sent to DM',
          image: infoImage,
          okButton: 'OK',
        })
        return
      }

      await this.$refs.warningDialog.show({
        title: 'Hidden Roll',
        message: 'DM not available.',
        okButton: 'OK',
      })
    },
    buildArtefactItemValueDisplay(item = {}) {
      const lowerNumericValue = Number(item?.value_lower)
      const upperNumericValue = Number(item?.value_upper)
      const hasLower =
        item?.value_lower !== null &&
        item?.value_lower !== undefined &&
        item?.value_lower !== '' &&
        Number.isFinite(lowerNumericValue) &&
        lowerNumericValue !== 0
      const hasUpper =
        item?.value_upper !== null &&
        item?.value_upper !== undefined &&
        item?.value_upper !== '' &&
        Number.isFinite(upperNumericValue) &&
        upperNumericValue !== 0

      if (!hasLower) {
        return ''
      }

      const lowerValue = this.formatItemValueNumber(item.value_lower)
      if (!lowerValue) {
        return ''
      }

      const upperValue = hasUpper
        ? this.formatItemValueNumber(item.value_upper)
        : ''
      const currencySuffix = this.getItemCurrencySuffix(item?.currency)
      const valueDisplay = upperValue
        ? `${lowerValue}-${upperValue}${currencySuffix}`
        : `${lowerValue}${currencySuffix}`

      return item?.estimated_value ? `${valueDisplay} (E)` : valueDisplay
    },
    //=============================================
    // manage character change
    //-------------------------
    async manageCharacterChange() {
      console.log('-------------------------------------')
      console.log('IN manageCharacterChange....')

      const pendingChanges = Array.isArray(this.pending_changes)
        ? this.pending_changes.slice()
        : []

      if (pendingChanges.length === 0) {
        return
      }

      this.pending_changes = []

      //----------------------------------------
      // set args for managing character change
      //----------------------------------------
      const original = {
        profile: this.original_profile,
        chars: this.original_chars,
        skills: this.original_skills,
        runes: this.original_runes,
        items: this.original_items,
        counts: this.original_counts,
        armour_locations: this.original_armour_locations,
        campaign_notes: this.original_campaign_notes,
      }

      const changes = {
        profile: this.changed_profile,
        chars: this.changed_chars,
        skills: this.changed_skills,
        runes: this.changed_runes,
        items: this.changed_items,
        counts: this.changed_counts,
        armour_locations: this.changed_armour_locations,
        campaign_notes: this.changed_campaign_notes,
      }

      const res = processChange({
        original,
        changes,
        character: this.local_character,
        change: pendingChanges[pendingChanges.length - 1] ?? this.change,
        changesQueue: pendingChanges,
        isNewCharacterLoad: this.isNewCharacterLoad,
      })

      if (res) {
        //--------------------------------
        // update character & change sets
        //--------------------------------
        this.local_character = res.this_character
        this.refreshCharacterEncState()
        this.changed_profile = res.these_changes.profile
        this.changed_chars = res.these_changes.chars
        this.changed_skills = res.these_changes.skills
        this.changed_runes = res.these_changes.runes
        this.changed_items = res.these_changes.items
        this.changed_counts = res.these_changes.counts
        this.changed_campaign_notes = res.these_changes.campaign_notes

        //-------------------------------------------------------------------------
        // where need to remove equipped armour (where item changed from equipped)
        //-------------------------------------------------------------------------
        if (res.remove_equipped_armour) {
          console.log(
            'off to remove equipped armour for item id = ',
            res.remove_equipped_armour,
          )
          // const equipped_armour = this.local_character.items.find((item) => item.id === res.remove_equipped_armour)
          // console.log('EQUIPPED ARMOUR TO REMOVE = ', equipped_armour)

          this.handleRemoveEquippedArmour({
            armour_id: res.remove_equipped_armour,
          })
        }

        //--------------------------------------------------------------------
        // where need to add equipped armour (where item changed to equipped)
        //--------------------------------------------------------------------
        if (res.add_equipped_armour) {
          console.log(
            'off to add equipped armour for item id = ',
            res.add_equipped_armour,
          )

          // get equipped armour item object
          const equipped_armour = this.local_character.items.find(
            (item) => item.id === res.add_equipped_armour,
          )

          if (!equipped_armour) {
            console.error('EQUIPPED ARMOUR ITEM NOT FOUND IN CHARACTER ITEMS')
            return
          }

          // get static armour item object (use the underlying static item id
          // from the equipped character item we just found)
          const static_armour = this.$store.getters[
            'characters/getStaticItemById'
          ](equipped_armour.item)

          if (!static_armour) {
            console.error('STATIC ARMOUR ITEM NOT FOUND IN STORE')
            return
          }

          this.handleAddEquippedArmour({
            static_armour: static_armour,
            equipped_armour: equipped_armour,
            character: this.local_character,
          })
        }

        //------------------------------------------------------------------------------
        // where container contents need recalculating (e.g. container/tally changes)
        //------------------------------------------------------------------------------
        if (res.calc_container_contents) {
          console.log(
            'off to recalc container contents for container id = ',
            res.calc_container_contents,
          )
          this.handleCalcContainerContents({
            container_id: res.calc_container_contents,
          })
        }

        //-------------------------------------------------------------
        // where need to clear item of runes/adjs (e.g. tally_qty = 0)
        //-------------------------------------------------------------
        if (res.clear_item) {
          console.log(
            'off to clear item of runes/adjs for item id = ',
            res.clear_item,
          )
          this.handleClearItem({ item_id: res.clear_item })
        }

        //-----------------------------------------------------------------------
        // where a rune's character_item link changed, update all related adjs
        //-----------------------------------------------------------------------
        if (res.update_rune_item_adjs && res.update_rune_item_adjs.rune) {
          console.log(
            'off to handle rune item adj changes = ',
            res.update_rune_item_adjs,
          )
          this.handleRuneItemAdjChanges(res.update_rune_item_adjs)
        }

        //------------------------------------------------------------------------------
        // where adjs returned e.g. ability change (shield/second-weapon added/removed)
        //------------------------------------------------------------------------------
        if (res.new_adjs && res.new_adjs.length > 0) {
          console.log(
            'NEW ADJS RETURNED FROM MANAGE CHARACTER CHANGE = ',
            res.new_adjs,
          )
          //------------
          // apply adjs
          //------------
          this.autoApplyAdjs(res.new_adjs)
        }
        //----------------------------------------------------------------------------------------------------
        // EXISTING RUNE: where skill addition required (runecasting skill needed following rune integration)
        //----------------------------------------------------------------------------------------------------
        if (res.new_casting_skill_rune) {
          console.log('off to add skill = ', res.new_casting_skill_rune)
          await this.manageRunecastingChange({
            requireRuneCastingSkill: true,
            requireApplyRuneAdjs: true,
            rune: res.new_casting_skill_rune,
          })
          await this.manageRuneAdjChanges({
            new_casting_skill: true,
            rune: res.new_casting_skill_rune,
          })
          //----------------------------------------------------------------------------------------------------------
          // EXISTING RUNE: where skill adjs apply required (runecasting skill adjs needed following rune activation)
          //-----------------------------------------------------------------------------------------------------------
        } else if (res.apply_rune_adjs_rune) {
          console.log('off to apply skill adjs = ', res.apply_rune_adjs_rune)
          await this.manageRunecastingChange({
            requireRuneCastingSkill: false,
            requireApplyRuneAdjs: true,
            rune: res.apply_rune_adjs_rune,
          })
          await this.manageRuneAdjChanges({
            new_casting_skill: false,
            rune: res.apply_rune_adjs_rune,
          })
        }

        if (pendingChanges.some((change) => change?.type === 'runes')) {
          this.recalculatePowAndMagicPoints()
        }

        if (pendingChanges.some((change) => change?.type === 'items')) {
          await this.syncCharacterOverloadingState({
            showTransitionPopup: true,
          })
        }
      } else {
        console.error('NO RESULTS FROM PROCESS CHARACTER CHANGE')
      }
      //--------------
      // clear change
      //--------------
      this.prev_change =
        pendingChanges[pendingChanges.length - 1] ?? this.change
      this.change = null
    },
    //=============================================
    // auto apply adjs
    //-----------------
    autoApplyAdjs(adjs) {
      console.log('-------------------------------------')
      console.log('IN autoApplyAdjs.... adjs = ', adjs)
      //------------------
      // get adj payloads
      //------------------
      const payloads = getAutoAdjPayloads(adjs)
      payloads.forEach((payload) => {
        console.log('AUTO APPLYING ADJ PAYLOAD = ', payload)
        //-------------------------------
        // process adjs for this payload
        //-------------------------------
        this.handleCalcTotalsRebuildReasonAppliedAdjs(payload)
      })
    },
    //=============================================
    // set view
    //----------
    setView(view) {
      this.setCurrentView(view)
    },
    //=============================================
    // get config values
    //-------------------
    getConfigValues() {
      // get config
      this.config = this.getConfig()

      if (!this.config) {
        throw new Error('Character config is unavailable.')
      }

      // load counter min/max values (abilities)
      this.max_skill = this.config.max_skill
      // load counter min/max values (abilities)
      this.sr_min = this.config.min_sr
      this.sr_max = this.config.max_sr
      this.ca_min = this.config.min_ca
      this.ca_max = this.config.max_ca
      this.cr_min = this.config.min_cr
      this.cr_max = this.config.max_cr
      //
      this.default_race = this.config.default_race
      //
      this.char_default_val = this.config.val_default_char
      this.char_default_min = this.config.min_default_char
      this.char_default_max = this.config.max_default_char
    },
    //=============================================
    // load character
    //----------------
    loadCharacter(existing_character) {
      // deep copy of character from prop to ensure derived values reset on exit
      this.local_character = copyObjectNoRef(existing_character)

      // initialise values/perform calcs
      this.local_character = loadCharacterValues(
        this.local_character,
        this.char_default_val,
        this.char_default_min,
        this.char_default_max,
      )
      this.refreshCharacterEncState()
      // console.log('=================================================')
      // console.log('LOCAL CHARACTER LOADED....')
      // console.log(this.local_character)
    },
    //=============================================
    // set character
    //---------------
    setCharacter() {
      console.log('=====================================')
      console.log('IN setCharacter....')
      //------------------------------------------
      // deep copy of character to ensure no refs
      //------------------------------------------
      this.local_character = { ...setCharacterValues(this.default_race) }
      if (this.listScope === 'mine') {
        const userProfile = this.getUserProfile() || {}

        this.local_character.arena_name = ''
        this.local_character.arena = null
        this.local_character.player = null
        this.local_character.player_name =
          userProfile.user_name || userProfile.first_name || ''
      }
      //---------------------
      // load default skills
      //---------------------
      let default_skills = this.getDefaultSkills()
      //--------------------------------------------
      // convert default skills to character skills
      //--------------------------------------------
      this.local_character.skills = convertDefaultSkills(default_skills)
      //--------------------
      // load starter items
      //--------------------
      let starter_items = this.getStarterItems()
      this.local_character.items = convertStarterItems(starter_items)
      //---------------------------
      // load race armour locations
      //----------------------------
      const getRace = this.$store.getters['races/getRaceByName']
      const allRaces = this.$store.getters['races/getRaces'] || []
      const characterRaces =
        this.$store.getters['races/getCharacterRaces'] || []
      const availableRaces =
        characterRaces.length > 0 ? characterRaces : allRaces
      const race =
        (typeof getRace === 'function'
          ? getRace(this.local_character.race_name)
          : null) ||
        availableRaces.find(
          (entry) => entry.name === this.local_character.race_name,
        ) ||
        allRaces.find(
          (entry) => entry.name === this.local_character.race_name,
        ) ||
        availableRaces[0] ||
        allRaces[0] ||
        null
      const locationHPs = calcLocationHPs({
        body_type: race?.body_type_name || race?.body_type || 'Humanoid',
        siz: this.local_character.siz_cur,
        con: this.local_character.con_cur,
        con_wounds: this.local_character.con_wounds_tot,
      })
      console.log(
        'XX race worn armour locations = ',
        race.worn_armour_locations,
      )
      this.local_character.armour_locations = convertRaceArmourLocations(
        race?.worn_armour_locations || [],
        locationHPs,
      )
      console.log(
        'character armour locations = ',
        this.local_character.armour_locations,
      )
      //--------------------
      // load starter counts
      //--------------------
      let starter_counts = this.getStarterCounts()
      this.local_character.counts = convertStarterCounts(starter_counts)
      //--------------------
      // initialise ENC fields
      //--------------------
      // For new characters, compute a client-side cur_enc from
      // starter items so that the summary and over-encumbered
      // checks do not reuse values from a previously viewed
      // character.
      const items = this.local_character.items || []
      this.local_character.cur_enc = this.calculateCurrentEnc(items)
    },
    //=============================================
    // handle update
    //---------------
    handleUpdate(change) {
      console.log('-------------------------------------')
      console.log('IN handleUpdate.... change = ', window.$deepCopy(change))
      //----------------------------------
      // perform update (will fire watch)
      //----------------------------------
      if (!isEmpty(change)) {
        if (change.type === 'skills' && change.notifyOnly) {
          this.showSkillNotification({
            action: change.action || 'updated',
            skillName: change.name,
          })
          return
        }

        if (change.type === 'armour_locations') {
          const armourLocation =
            change.object || getArrayObject(this.local_character, change)

          if (armourLocation) {
            this.changed_armour_locations = trackArmourLocationChanges({
              armour_location: armourLocation,
              original_armour_locations: this.original_armour_locations,
              changed_armour_locations: this.changed_armour_locations,
            })
          }

          return
        }

        //======================================================================
        // A. TABLE UPDATE
        //======================================================================
        // examples:
        // - type = skills / key = training / name = Brute Force / value = 1
        // - id="6cb24c3d-b217-4820-a39c-ccd0e2e1ba36" / idx=1 / key="starter" / name="Disorder Rune" / type="runes" / value=false
        //----------------------------------------------------------------------
        if (change.name) {
          console.log(
            'ROUTE 1 - UPDATING OBJECT IN ARRAY - TYPE = ' +
              change.type +
              ' / name = ' +
              change.name +
              ' / id = ' +
              change.id +
              ' / value = ' +
              change.value +
              ' / idx = ' +
              change.idx,
          )
          console.log(
            'LOCAL CHARACTER = ',
            this.$deepCopy(this.local_character),
          )
          //-------------------
          // get change object
          //-------------------
          const change_object = getArrayObject(this.local_character, change)
          //---------------------------
          // check found change object
          //---------------------------
          if (change_object) {
            console.log('GOT ARRAY OBJECT RESULT = ', change_object)
            //-----------------------------------
            // defensive check for undefined key
            //-----------------------------------
            if ('undefined' in change_object) {
              console.error('>>>> UNDEFINED KEY ADDED!')
              // console.trace()
            } else {
              //--------------------------------------------------------------------------------
              // update object field directly (updates local character by ref - triggers watch)
              //--------------------------------------------------------------------------------
              const previousValue = change_object[change.key]
              change.previousValue = previousValue
              change_object[change.key] = change.value

              // Preserve UI-only artefact editability state on the
              // in-memory character item so reopening/remounting the
              // item card during an edit session does not recompute the
              // _static_* flags from the newly selected values.
              if (change.ui_state && typeof change.ui_state === 'object') {
                Object.assign(change_object, change.ui_state)
              }

              if (
                change.type === 'items' &&
                [
                  'value_lower',
                  'value_upper',
                  'currency',
                  'estimated_value',
                ].includes(change.key)
              ) {
                change_object.value =
                  this.buildArtefactItemValueDisplay(change_object)
              }
              //---------------------------------------------
              // add changed object to change (for tracking)
              //---------------------------------------------
              change.object = change_object
              this.pending_changes.push(change)
              //---------------
              // record change (passed for processing in manageCharacterChange)
              //---------------
              this.change = change
              console.log(
                'UPDATED ARRAY OBJECT field = ' +
                  change.key +
                  ' / value = ' +
                  change_object[change.key],
              )

              if (
                change.type === 'skills' &&
                change.key !== 'favourite' &&
                !change.suppressNotification &&
                previousValue !== change.value
              ) {
                this.showSkillNotification({
                  action: 'updated',
                  skillName: change.name,
                })
              }
            }
            //--------------------------------------
            // defensive check for no change object
            //--------------------------------------
          } else {
            console.warn(
              'NO CHANGE OBJECT FOUND FROM GETARRAYOBJECT = ' + change.name,
            )
          }
          //=====================================================================
          // B. INDIVIDUAL UPDATE [no arrays to deal with]
          //=====================================================================
        } else {
          //----------------------------------------------
          // e.g. type = profile / key = age / value = 35
          //----------------------------------------------
          console.log(
            'ROUTE 2 - UPDATING SIMPLE FIELD - TYPE = ' +
              change.type +
              ' / key = ' +
              change.key,
          )
          //---------------
          // record change
          //---------------
          this.change = change
          this.pending_changes.push(change)
          //-----------------------------------
          // individual character field change
          //-----------------------------------
          this.local_character[change.key] = change.value
          if (change.key === 'race_name') {
            this.applyRaceDefaultsToCharacter(change.value)
          } else if (change.key === 'wounds') {
            this.local_character = syncCharacterCombatStateFromArmourLocations(
              this.local_character,
              {
                manualWounds: change.value,
              },
            )
          } else if (change.key === 'fatigue_override') {
            this.local_character = syncCharacterCombatStateFromArmourLocations(
              this.local_character,
              {
                overrideFatigue: change.value,
              },
            )
          }
          console.log(
            'UPDATED SIMPLE FIELD!! field = ' +
              this.local_character[change.key],
          )
        }
      }
    },
    applyRaceDefaultsToCharacter(raceName) {
      if (!this.local_character) {
        return
      }

      const raceDefaults = setCharacterValues(raceName || this.default_race)
      const prefixes = getCharacteristicPrefixes()

      prefixes.forEach((prefix) => {
        this.local_character[`${prefix}_rolled`] =
          raceDefaults[`${prefix}_rolled`]
        this.local_character[`${prefix}_min`] = raceDefaults[`${prefix}_min`]
        this.local_character[`${prefix}_max`] = raceDefaults[`${prefix}_max`]
      })

      this.local_character.move_base = raceDefaults.move_base
      this.local_character.move_fly_base = raceDefaults.move_fly_base
      this.local_character.max_enc = raceDefaults.max_enc

      let nextChangedChars = this.changed_chars

      prefixes.forEach((prefix) => {
        const result = processCharacteristicChange({
          pfx: prefix,
          original_chars: this.original_chars,
          changed_chars: nextChangedChars,
          isNewCharacterLoad: false,
          character: this.local_character,
        })

        this.local_character = result.character
        nextChangedChars = result.changed_chars
      })

      this.changed_chars = nextChangedChars
      this.syncCharacteristicEntriesFromFlatValues()
    },
    syncCharacteristicEntriesFromFlatValues() {
      if (!this.local_character) {
        return
      }

      const prefixes = getCharacteristicPrefixes()

      if (!Array.isArray(this.local_character.characteristics)) {
        this.local_character.characteristics = []
      }

      prefixes.forEach((prefix) => {
        let characteristic = this.local_character.characteristics.find(
          (entry) => entry?.name === prefix,
        )

        if (!characteristic) {
          characteristic = {
            id: null,
            name: prefix,
            characteristic_adjs: [],
            integrated_rune_characteristic_adjs: [],
          }
          this.local_character.characteristics.push(characteristic)
        }

        characteristic.rolled = this.local_character[`${prefix}_rolled`]
        characteristic.cur = this.local_character[`${prefix}_cur`]
        characteristic.improvs_tot =
          this.local_character[`${prefix}_improvs_tot`]
        characteristic.improvs = this.local_character[`${prefix}_improvs_tot`]
        characteristic.incr_percent =
          this.local_character[`${prefix}_incr_percent`]
        characteristic.adjs_tot = this.local_character[`${prefix}_adjs_tot`]

        if (prefix === 'pow') {
          characteristic.integs_tot = this.local_character.pow_integs_tot
        }
      })
    },
    recalculatePowAndMagicPoints() {
      if (!this.local_character) {
        return
      }

      this.local_character = calcCharacteristicValues({
        pfx: 'pow',
        character: this.local_character,
      })
      this.local_character = calcAbilities({
        pfx: 'pow',
        character: this.local_character,
      })

      this.local_character.mp_cur = Number(this.local_character.mp) || 0

      this.syncCharacteristicEntriesFromFlatValues()
    },
    handleSyncCombatState(character) {
      if (!character) {
        return
      }

      this.local_character = character
    },
    enforceProfileTab() {
      this.activeTab = 'profile'
      this.$nextTick(() => {
        const profileTab = document.getElementById('pills-profile-tab')
        if (profileTab) {
          profileTab.click()
        }
      })
    },
    //=============================================
    // handle calc container contents
    //--------------------------------
    handleCalcContainerContents({ container_id }) {
      console.log('--------------------------------')
      console.log(
        'IN handleCalcContainerContents for container id = ',
        container_id,
      )
      //-----------------------------------------------------------------------
      // calc container contents (returns total/contents enc & contents count)
      //-----------------------------------------------------------------------
      const container_contents = calcContainerContents({
        container_id,
        character_items: this.local_character.items,
      })
      //------------------------
      // got container contents
      //------------------------
      if (container_contents) {
        console.log('GOT container contents = ', container_contents)
        //---------------------
        // find container item
        //---------------------
        // console.log(
        //   'character items BEFORE container contents update = ',
        //   this.$deepCopy(this.local_character.items),
        // )
        const container_item = this.local_character.items.find(
          (item) => item.id === container_id,
        )
        //---------------
        // got container
        //---------------
        if (container_item) {
          //----------------------------------
          // update contents (triggers watch)
          //----------------------------------
          container_item.tot_enc = container_contents.tot_enc
          container_item.contents_enc = container_contents.contents_enc
          container_item.contents_count_direct =
            container_contents.contents_count_direct
          container_item.contents_count_total =
            container_contents.contents_count_total
          this.refreshCharacterEncState()
          console.log(
            `UPDATED container item ${container_item.name} contents = ${container_contents}`,
          )

          //------------------------------------------------------
          // if this container itself is stored in another one,
          // bubble the recalculation up to its parent container
          //------------------------------------------------------
          const parentContainerId = container_item.container ?? null
          if (parentContainerId) {
            console.log(
              `Bubbling container contents recalculation up to parent container id = ${parentContainerId}`,
            )
            this.handleCalcContainerContents({
              container_id: parentContainerId,
            })
          }
        } else {
          console.error('NO CONTAINER ITEM FOUND TO UPDATE CONTENTS!!')
        }
      } else {
        console.error(
          'NO container contents returned from calcContainerContents!!',
        )
      }
    },
    //=============================================
    // handle clear item
    //-------------------
    handleClearItem({ item_id }) {
      console.log('--------------------------------')
      console.log('IN handleClearItem for item id = ', item_id)
      //-------------------
      // get item to clear
      //-------------------
      const clear_item = this.local_character.items.find(
        (item) => item.id === item_id,
      )
      //----------------
      // got clear item
      //----------------
      if (clear_item) {
        console.log('GOT clear item = ', window.$deepCopy(clear_item))

        //-------------------------------
        // A. delete any character runes
        //-------------------------------
        // Use the character-wide runes list to find all runes
        // linked to this item (integrated or not), then delete
        // them via the normal rune deletion flow. This ensures
        // any associated Runecasting skill(s) and rune adjs are
        // also removed, as handled in handleDeleteRune.
        const linkedRunes = (this.local_character.runes || []).filter((r) =>
          this.isEntryLinkedToCharacterItem(r, item_id),
        )
        if (Array.isArray(linkedRunes) && linkedRunes.length > 0) {
          linkedRunes.forEach((rune) => {
            console.log(
              `Deleting associated rune ${rune.name} from item ${clear_item.name}`,
            )
            this.handleDeleteRune(rune, { suppressConfirm: true })
          })
        }

        //------------------------
        // clear any item.runes UI
        //------------------------
        if (Array.isArray(clear_item.runes) && clear_item.runes.length > 0) {
          clear_item.runes = []
        }

        //---------------------------------------
        // A2. delete any item-granted spells
        //---------------------------------------
        // Find all spells whose source is this item (CharacterItemSpell
        // rows surfaced on the frontend) and delete them via the normal
        // spell deletion flow so that associated casting skills and
        // change tracking stay in sync.
        const linkedSpells = (this.local_character.spells || []).filter((s) =>
          this.isEntryLinkedToCharacterItem(s, item_id),
        )
        if (Array.isArray(linkedSpells) && linkedSpells.length > 0) {
          linkedSpells.forEach((spell) => {
            console.log(
              `Deleting associated item spell ${spell.name} from item ${clear_item.name}`,
            )
            this.handleDeleteSpell(spell)
          })
        }

        //---------------------------
        // clear any item.spells UI
        //---------------------------
        if (Array.isArray(clear_item.spells) && clear_item.spells.length > 0) {
          clear_item.spells = []
        }

        //---------------------------------------
        // B. clear item location / attach point
        //---------------------------------------
        if (clear_item.worn_location) {
          clear_item.worn_location = null
          console.log(`Cleared worn_location for item ${clear_item.name}`)
        }
        if (clear_item.attach_location) {
          clear_item.attach_location = null
          console.log(`Cleared attach_location for item ${clear_item.name}`)
        }
        if (clear_item.container) {
          clear_item.container = null
          console.log(`Cleared container for item ${clear_item.name}`)
        }

        //----------------------------------------
        // C. clear characteristics adjs for item
        //-----------------------------------------
        if (
          this.local_character.characteristic_adjs &&
          this.local_character.characteristic_adjs.length > 0
        ) {
          const removed_characteristic_adjs =
            this.local_character.characteristic_adjs.filter(
              (adj) => adj.character_item === item_id,
            )
          const remaining_characteristic_adjs =
            this.local_character.characteristic_adjs.filter(
              (adj) => adj.character_item !== item_id,
            )
          this.local_character.characteristic_adjs =
            remaining_characteristic_adjs
          console.log(`Cleared characteristic adjs for item ${clear_item.name}`)
          //--------------------------------------------------------------
          // rebuild characteristic adj totals/reasons for affected types
          //--------------------------------------------------------------
          if (removed_characteristic_adjs.length > 0) {
            this.local_character = processResetTotalsReasonAdjsHarness({
              adjs: removed_characteristic_adjs,
              category: 'characteristic',
              character: this.local_character,
            })
            this.local_character =
              processCalcTotalsRebuildReasonAppliedAdjsHarness({
                adjs: removed_characteristic_adjs,
                category: 'characteristic',
                character: this.local_character,
                add_only: false,
              })
          }
        }
        //--------------------------------
        // D. clear ability adjs for item
        //--------------------------------
        if (
          this.local_character.ability_adjs &&
          this.local_character.ability_adjs.length > 0
        ) {
          const removed_ability_adjs = this.local_character.ability_adjs.filter(
            (adj) => adj.character_item === item_id,
          )
          const remaining_ability_adjs =
            this.local_character.ability_adjs.filter(
              (adj) => adj.character_item !== item_id,
            )
          this.local_character.ability_adjs = remaining_ability_adjs
          console.log(`Cleared ability adjs for item ${clear_item.name}`)
          //--------------------------------------------------------
          // rebuild ability adj totals/reasons for affected types
          //--------------------------------------------------------
          if (removed_ability_adjs.length > 0) {
            this.local_character = processResetTotalsReasonAdjsHarness({
              adjs: removed_ability_adjs,
              category: 'ability',
              character: this.local_character,
            })
            this.local_character =
              processCalcTotalsRebuildReasonAppliedAdjsHarness({
                adjs: removed_ability_adjs,
                category: 'ability',
                character: this.local_character,
                add_only: false,
              })
          }
        }
        //------------------------------
        // E. clear skill adjs for item
        //------------------------------
        if (
          this.local_character.skill_adjs &&
          this.local_character.skill_adjs.length > 0
        ) {
          const removed_skill_adjs = this.local_character.skill_adjs.filter(
            (adj) => adj.character_item === item_id,
          )
          const remaining_skill_adjs = this.local_character.skill_adjs.filter(
            (adj) => adj.character_item !== item_id,
          )
          this.local_character.skill_adjs = remaining_skill_adjs
          console.log(`Cleared skill adjs for item ${clear_item.name}`)
          //--------------------------------------------------------------
          // rebuild skill adj totals/reasons for affected skills
          // (use removed adjs so we still reset skills even when this was
          //  the last adj for that skill and no entries remain)
          //--------------------------------------------------------------
          if (removed_skill_adjs.length > 0) {
            console.log('Rebuilding skill adj totals/reasons after item clear')
            this.local_character = processResetTotalsReasonAdjsHarness({
              adjs: removed_skill_adjs,
              category: 'skill',
              character: this.local_character,
            })
            this.local_character =
              processCalcTotalsRebuildReasonAppliedAdjsHarness({
                adjs: removed_skill_adjs,
                category: 'skill',
                character: this.local_character,
                add_only: false,
              })
            //---------------------------------------------
            // also recalc OPTIONAL adj counts & reasons
            //---------------------------------------------
            const affectedTypes = [
              ...new Set(removed_skill_adjs.map((adj) => adj.name)),
            ]
            affectedTypes.forEach((type) => {
              const optionalNow = (
                this.local_character.skill_adjs || []
              ).filter((adj) => adj.name === type && adj.applied === false)
              this.local_character = processCalcCountRebuildReasonOptAdjs({
                payload: {
                  category: 'skill',
                  type,
                  data: optionalNow,
                },
                character: this.local_character,
              })
            })
          }
        }
      } else {
        console.error('NO ITEM TO CLEAR!! ID = ', item_id)
      }
    },
    //=============================================
    // handle add count
    //------------------
    async handleAddCount(parms) {
      console.log('--------------------------------')
      console.log('IN handleAddCount')
      console.log('parms = ', parms)
      //----------------------→
      // process add new count
      //-----------------------
      let res = processAddCount({
        parms: parms,
        original_counts: this.original_counts,
        changed_counts: this.changed_counts,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = res.character
        this.local_character.counts = [...this.local_character.counts]
        this.changed_counts = res.changed_counts

        console.log('2. res.new_count = ', res.new_count)
        console.log(
          '3. character counts AFTER processAddCount: ',
          this.$deepCopy(this.local_character.counts),
        )
        console.log('END!!!!!! new count:', res.new_count.name)
      } else {
        console.error('NO RESULTS FROM PROCESS ADD COUNT!!!!!')
      }
    },
    //=============================================
    // handle add campaign note
    //--------------------------
    async handleAddCampaignNote(parms) {
      console.log('--------------------------------')
      console.log('IN handleAddCampaignNote')
      console.log('parms = ', parms)
      //-------------------------------
      // process add new campaign note
      //-------------------------------
      let res = processAddCampaignNote({
        parms: parms,
        original_campaign_notes: this.original_campaign_notes,
        changed_campaign_notes: this.changed_campaign_notes,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        console.log('1. res = ', res)
        this.local_character = res.character
        this.local_character.campaign_notes = [
          ...this.local_character.campaign_notes,
        ]
        this.changed_campaign_notes = res.changed_campaign_notes

        console.log('2. res.new_note = ', res.new_campaign_note)

        console.log(
          '3. this.changed_campaign_notes = ',
          this.$deepCopy(this.changed_campaign_notes),
        )

        console.log(
          '3. character notes AFTER processAddCampaignNote: ',
          this.$deepCopy(this.local_character.campaign_notes),
        )
        console.log(
          'END!!!!!! new campaign note:',
          res.new_campaign_note.updated_at,
        )
      } else {
        console.error('NO RESULTS FROM PROCESS ADD CAMPAIGN NOTE!!!!!')
      }
    },
    //=============================================
    // handle add spell
    //------------------
    async handleAddSpell(parms) {
      console.log('--------------------------------')
      console.log('IN handleAddSpell')
      console.log('parms = ', parms)
      //------------------
      // get static spell
      //------------------
      let static_spell = parms.payload.spell
        ? this.$store.getters['characters/getStaticSpellById'](
            parms.payload.spell,
          )
        : null
      console.log('1. static_spell = ', static_spell)
      //----------------------
      // process add new spell
      //----------------------
      let res = processAddSpell({
        parms: parms,
        original_spells: this.original_spells,
        changed_spells: this.changed_spells,
        static_spell: static_spell,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = res.character
        this.local_character.spells = [...this.local_character.spells]
        this.changed_spells = res.changed_spells

        //---------------------------------------------------------------
        // add casting skill if needed (e.g. for specific sorcery spell)
        //---------------------------------------------------------------
        if (res.casting_skill_applicable) {
          console.log(
            'casting skill changes to apply = ',
            res.casting_skill_applicable,
          )
          //---------------------------------------------
          // create Sorcery Casting skill for this spell
          // (purely frontend; backend also creates dedicated
          //  CharacterSkill which will be reconciled on save)
          //---------------------------------------------
          await this.manageSorceryCastingChange({ spell: res.new_spell })
        }

        console.log('2. res.new_spell = ', res.new_spell)
        console.log(
          '3. character spells AFTER processAddSpell: ',
          this.$deepCopy(this.local_character.spells),
        )
        console.log('END!!!!!! new spell:', res.new_spell.name)
      } else {
        console.error('NO RESULTS FROM PROCESS ADD SPELL!!!!!')
      }
    },
    //=============================================
    // handle delete spell
    //------------------
    async handleDeleteSpell(spell) {
      console.log('--------------------------------')
      console.log('IN handleDeleteSpell')
      console.log('spell = ', spell)
      const spellType = spell?.type || ''
      const spellDisplayName = spell?.display_name || spell?.name || ''
      const spellCastingSkillId =
        spell?.casting_skill ?? spell?.casting_skill_id
      const linkedSorceryCastingSkill =
        spellType === 'Sorcery'
          ? (this.local_character.skills || []).find(
              (s) =>
                s &&
                ((spellCastingSkillId && s.id === spellCastingSkillId) ||
                  (s.name === 'Sorcery Casting' &&
                    s.display_name === spellDisplayName)),
            )
          : null
      //-------------------------
      // go process delete spell
      //-------------------------
      let res = processDeleteSpell({
        spell: spell,
        original_spells: this.original_spells,
        changed_spells: this.changed_spells,
        character: this.local_character,
      })
      //-----------------
      // handle response
      //-----------------
      if (res) {
        this.local_character = res.character
        this.changed_spells = res.updated_changed_spells

        if (linkedSorceryCastingSkill) {
          const skillDeleteRes = processDeleteSkill({
            skill: linkedSorceryCastingSkill,
            original_skills: this.original_skills,
            changed_skills: this.changed_skills,
            character: this.local_character,
          })

          if (skillDeleteRes) {
            this.local_character = skillDeleteRes.character
            this.changed_skills = skillDeleteRes.updated_changed_skills
          }
        }

        //-----------------------------------------------
        // replace spells/skills with themselves (force reactivity)
        //-----------------------------------------------
        this.local_character = {
          ...this.local_character,
          spells: [...(this.local_character.spells || [])],
          skills: [...(this.local_character.skills || [])],
        }
      } else {
        console.error('NO SPELL DELETED')
      }
    },
    //=============================================
    // handle add item
    //------------------
    async handleAddItem(parms) {
      console.log('--------------------------------')
      console.log('IN handleAddItem')
      console.log('parms = ', parms)

      const isWeaponLikeStaticItem = (item) => {
        if (!item || typeof item !== 'object') {
          return false
        }

        const type = String(item.type ?? '')
          .trim()
          .toLowerCase()
        const artefactType = String(item.artefact_type ?? '')
          .trim()
          .toLowerCase()

        return (
          type === 'melee weapon' ||
          type === 'ranged weapon' ||
          artefactType === 'melee weapon' ||
          artefactType === 'ranged weapon'
        )
      }
      //-----------------
      // get static item
      //-----------------
      let static_item = parms.payload.item
        ? this.$store.getters['characters/getStaticItemById'](
            parms.payload.item,
          )
        : null
      console.log('1. static_item = ', static_item)
      //----------------------
      // process add new item
      //----------------------
      let res = processAddItem({
        parms: parms,
        original_items: this.original_items,
        changed_items: this.changed_items,
        static_item: static_item,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = res.character
        this.local_character.items = [...this.local_character.items]
        this.changed_items = res.changed_items
        //---------------------------------------------
        // ensure weapon skills exist for new weapon
        //---------------------------------------------
        if (isWeaponLikeStaticItem(static_item)) {
          const createdWeaponSkillNames = await this.manageWeaponSkillLinks({
            static_item,
          })

          const itemDisplayName =
            typeof res?.new_item?.display_name === 'string' &&
            res.new_item.display_name.trim() !== ''
              ? res.new_item.display_name.trim()
              : typeof res?.new_item?.name === 'string' &&
                  res.new_item.name.trim() !== ''
                ? res.new_item.name.trim()
                : typeof static_item?.display_name === 'string' &&
                    static_item.display_name.trim() !== ''
                  ? static_item.display_name.trim()
                  : typeof static_item?.name === 'string'
                    ? static_item.name.trim()
                    : 'Item'

          if (createdWeaponSkillNames.length > 0) {
            const skillAddedSentences = createdWeaponSkillNames
              .map((skillName) => `${skillName} skill added.`)
              .join(' ')

            showNotification(`${itemDisplayName} added. ${skillAddedSentences}`)
          } else {
            showNotification(`${itemDisplayName} added.`)
          }
        } else {
          const itemDisplayName =
            typeof res?.new_item?.display_name === 'string' &&
            res.new_item.display_name.trim() !== ''
              ? res.new_item.display_name.trim()
              : typeof res?.new_item?.name === 'string' &&
                  res.new_item.name.trim() !== ''
                ? res.new_item.name.trim()
                : typeof static_item?.display_name === 'string' &&
                    static_item.display_name.trim() !== ''
                  ? static_item.display_name.trim()
                  : typeof static_item?.name === 'string'
                    ? static_item.name.trim()
                    : 'Item'

          showNotification(`${itemDisplayName} added.`)
        }
        //-------------------------
        // process equipped armour
        //-------------------------
        if (res.equipped_armour) {
          console.log(
            'equipped armour changes to apply = ',
            res.equipped_armour,
          )
          //----------------------------------------------------------------------------------
          // call equipped armour handler (DONT NEED CHARACTER WATCH AS THIS PURELY FRONTEND)
          //----------------------------------------------------------------------------------
          this.handleAddEquippedArmour({
            static_armour: res.static_item,
            equipped_armour: res.new_item,
            character: this.local_character,
          })
        }
        //----------------------------------------------------
        // process any tagged containers (for contents calcs)
        //----------------------------------------------------
        if (res.calc_containers && res.calc_containers.length > 0) {
          console.log('container changes to apply = ', res.calc_containers)
          //--------------------
          // loop container ids
          //--------------------
          res.calc_containers.forEach((container_id) => {
            //-------------------------------------------------------------------------------
            // call container contents calc handler (will trigger watch to update character)
            //-------------------------------------------------------------------------------
            this.handleCalcContainerContents({ container_id })
          })
        }
        console.log('2. res.new_item = ', res.new_item)
        // console.log(
        //   '3. character items AFTER processAddItem: ',
        //   this.$deepCopy(this.local_character.items),
        // )
        await this.syncCharacterOverloadingState({
          showTransitionPopup: true,
        })
        console.log('END!!!!!! new item:', res.new_item.name)
      } else {
        console.error('NO RESULTS FROM PROCESS ADD ITEM!!!!!')
      }
    },
    //=============================================
    // manage weapon skill links
    //---------------------------
    // called when adding a new weapon item or
    // when seeding starter weapons for a new
    // character. For each static weapon skill
    // linked to the item, ensure the character
    // has a corresponding character-skill entry.
    //---------------------------------------------
    async manageWeaponSkillLinks({ static_item = null } = {}) {
      console.log('--------------------------------')
      console.log('IN manageWeaponSkillLinks')
      console.log('static_item = ', static_item)
      const createdSkillNames = []

      const isWeaponLikeStaticItem = (item) => {
        if (!item || typeof item !== 'object') {
          return false
        }

        const type = String(item.type ?? '')
          .trim()
          .toLowerCase()
        const artefactType = String(item.artefact_type ?? '')
          .trim()
          .toLowerCase()

        return (
          type === 'melee weapon' ||
          type === 'ranged weapon' ||
          artefactType === 'melee weapon' ||
          artefactType === 'ranged weapon'
        )
      }

      const normaliseSkillName = (value) =>
        typeof value === 'string' ? value.trim().toLowerCase() : ''

      if (!isWeaponLikeStaticItem(static_item)) {
        return createdSkillNames
      }

      const staticSkills = Array.isArray(static_item.skills)
        ? static_item.skills
        : []
      if (!staticSkills.length) {
        console.log(
          'No static weapon skills linked to item; skipping manageWeaponSkillLinks',
        )
        return createdSkillNames
      }

      const characterSkills = Array.isArray(this.local_character?.skills)
        ? this.local_character.skills
        : []

      for (const s of staticSkills) {
        if (!s) continue
        const skillName = s.name
        if (!skillName) continue

        const normalisedSkillName = normaliseSkillName(skillName)
        if (!normalisedSkillName) continue

        const alreadyHasSkill = characterSkills.some(
          (cs) =>
            cs &&
            (normaliseSkillName(cs.name) === normalisedSkillName ||
              normaliseSkillName(cs.display_name) === normalisedSkillName),
        )

        if (alreadyHasSkill) {
          console.log('Character already has skill for weapon = ', skillName)
          continue
        }

        // resolve the static skill definition from the store
        const staticSkill =
          this.$store.getters['characters/getStaticSkillByName'](skillName)

        if (!staticSkill) {
          console.error(
            'Static skill not found in store for weapon skill name = ',
            skillName,
          )
          continue
        }

        const add_parms = {
          name: staticSkill.name,
          payload: {
            id: createTempId(),
            display_name: null,
            character:
              this.local_character && this.local_character.id
                ? this.local_character.id
                : null,
            skill: staticSkill.id,
            ticks: 0,
            training: 0,
          },
        }

        console.log(
          'Adding missing weapon skill via handleAddSkill, parms = ',
          add_parms,
        )
        const createdSkillName = await this.handleAddSkill(add_parms, {
          suppressNotification: true,
        })

        if (createdSkillName) {
          createdSkillNames.push(createdSkillName)
        }
      }

      return createdSkillNames
    },
    //=============================================
    // handle remove equipped armour
    //-------------------------------
    async handleRemoveEquippedArmour({ armour_id }) {
      console.log('--------------------------------')
      console.log('IN handleRemoveEquippedArmour')
      console.log('armour_id = ', armour_id)
      //--------------------------------
      // process remove equipped armour
      //--------------------------------
      let res = processRemoveEquippedArmour({
        armour_id,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = syncArmourDexCharacteristicSkillAdj(res)
        this.local_character = loadSkillAdjsHarness(this.local_character)
        this.local_character = copyObjectNoRef(this.local_character)
        console.log(
          'character after processing remove equipped armour = ',
          this.$deepCopy(this.local_character),
        )
      } else {
        console.error('NO RESULTS FROM PROCESS REMOVE EQUIPPED ARMOUR!!!!!')
      }
    },
    //=============================================
    // handle add equipped armour
    //----------------------------
    async handleAddEquippedArmour({
      static_armour = null,
      equipped_armour = null,
      character = null,
    } = {}) {
      console.log('--------------------------------')
      console.log('IN handleAddEquippedArmour')
      console.log('equipped_armour = ', equipped_armour)
      //-----------------------------
      // process add equipped armour (new/existing armour with equipped = true)
      //-----------------------------
      let res = processAddEquippedArmour({
        static_armour,
        equipped_armour,
        character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = syncArmourDexCharacteristicSkillAdj(res)
        this.local_character = loadSkillAdjsHarness(this.local_character)
        this.local_character = copyObjectNoRef(this.local_character)
        console.log(
          'character after processing equipped armour = ',
          this.$deepCopy(this.local_character),
        )
      } else {
        console.error('NO RESULTS FROM PROCESS ADD EQUIPPED ARMOUR!!!!!')
      }
    },
    //=============================================
    // handle add skill
    //------------------
    async handleAddSkill(parms, options = {}) {
      console.log('--------------------------------')
      console.log('IN handleAddSkill')
      console.log('parms = ', parms)
      const { suppressNotification = false } = options
      //------------------
      // get static skill
      //------------------
      let static_skill = parms.payload.skill
        ? this.$store.getters['characters/getStaticSkill'](parms.payload.skill)
        : null
      console.log('1. static_skill = ', static_skill)

      if (
        static_skill?.parent?.name &&
        !hasCharacterSkill(
          this.local_character?.skills ?? [],
          static_skill.parent.name,
        )
      ) {
        await this.$refs.warningDialog.show({
          title: 'Add Character Skill',
          message: 'Parent character skill missing.',
          subject: `${static_skill.name} requires ${static_skill.parent.name}. Add the parent skill first.`,
          okButton: 'OK',
        })
        return null
      }

      // const apply_multiple_skills = static_skill ? static_skill.all_in_type == true : false
      console.log(
        '1b. character skill_adjs BEFORE processAddSkill: ',
        this.$deepCopy(this.local_character.skill_adjs),
      )
      console.log(
        '1c. character skills BEFORE processAddSkill: ',
        this.$deepCopy(this.local_character.skills),
      )
      //-----------------------
      // process add new skill
      //-----------------------
      let res = processAddSkill({
        parms: parms,
        original_skills: this.original_skills,
        changed_skills: this.changed_skills,
        static_skill: static_skill,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = res.character
        this.local_character.skills = [...this.local_character.skills]
        this.changed_skills = res.changed_skills

        // Ensure any newly added skill immediately receives current
        // fatigue-driven applied adjustments (and dependent skill adj reasons).
        const fatigueLevel =
          this.local_character?.fatigue_override ||
          this.local_character?.fatigue ||
          'Fresh'
        const fatigueDetails = getFatigueDetailsForLevel(fatigueLevel)
        this.local_character = syncCharacterFatigueAdjustments(
          this.local_character,
          fatigueDetails,
        )

        console.log(
          '1d. character skill_adjs AFTER processAddSkill: ',
          this.$deepCopy(this.local_character.skill_adjs),
        )
        console.log(
          '1e. character skills AFTER processAddSkill: ',
          this.$deepCopy(this.local_character.skills),
        )
        console.log('2a. res.new_skill = ', res.new_skill)
        //------------------------------
        // get skill_adjs for new skill   [where new runecasting skill/new rune - calc totals/reason builds via processApplyNewRuneAdjs]
        //------------------------------
        if (res.new_skill.name != 'Runecasting') {
          console.log(
            '5. getting skill_adjs for new skill = ',
            res.new_skill.name,
          )
          this.adj_category = 'skill'
          this.adj_type = res.new_skill.name
          const skill_adjs = this.filteredAdjs
          console.log('6. skill_adjs for new skill = ', skill_adjs)
          //-------------------------------------
          // calc skill adj totals for new skill
          //-------------------------------------
          console.log('7. go handleCalcTotalsRebuildReasonAppliedAdjs')
          this.handleCalcTotalsRebuildReasonAppliedAdjs({
            data: skill_adjs,
            category: this.adj_category,
            type: this.adj_type,
          })
          //-------------------------
          // reset adj category/type
          //-------------------------
          this.adj_category = ''
          this.adj_type = ''
          console.log(
            'END!!!!!! new skill:',
            res.new_skill.name,
            ' parent = ',
            res.new_skill.parent_name,
            ' formula = ',
            res.new_skill.formula,
          )
        } else {
          console.log('8. skipping skill_adjs for runecasting skill')
        }

        if (!suppressNotification) {
          this.showSkillNotification({
            action: 'added',
            skillName: res.new_skill?.name,
          })
        }

        return res.new_skill?.name || null
      }

      return null
    },
    //=============================================
    // handle add rune
    //-----------------
    async handleAddRune(parms) {
      console.log('--------------------------------')
      console.log('IN handleAddRune')
      console.log('parms = ', parms)
      /*
      -----------------------------------------------------------------------------------------------------------------
      SUMMARY:
      --------
      Headline Methods: processAddRune → processCopyStaticRuneAdjs → processApplyNewRuneAdjs → processCalcTotalsRebuildReasonAppliedAdjsHarness → processCalcTotalsRebuildReasonAppliedAdjs

      A. processAddRune (here):
      - add character.runes entry for new rune
      - copy adjs from static rune to new character-rune adjs arrays (if integrated & active)
      - track changes to runes for later commit to db
      - flag if runecasting skill required
      - flag if apply rune adjs required

      B. processCopyStaticRuneAdjs:
        - from static rune -> load into new character-rune adjs arrays

      C. handleAddSkill:
        - add runecasting skill where required

      D. processApplyNewRuneAdjs:
        - from character-rune adjs -> load into character skills, characteristics & abilities.

      E. processCalcTotalsRebuildReasonAppliedAdjsHarness/processCalcTotalsRebuildReasonAppliedAdjs:
        - build adj reasons & calc adj totals

      -----------------------------------------------------------------------------------------------------------------
      */
      //----------
      // declares
      //----------
      let res = null
      console.log(
        '1. =====>>>> local character before adding rune: ',
        this.$deepCopy(this.local_character),
      )
      console.log(
        '2. character skill_adjs BEFORE adding rune: ',
        this.$deepCopy(this.local_character.skill_adjs),
      )
      console.log(
        '2b. character skills BEFORE adding rune: ',
        this.$deepCopy(this.local_character.skills),
      )
      //------------------------------------------------------------
      // process add rune NB: integrity already managed in runecard
      //------------------------------------------------------------
      res = processAddRune({
        parms: parms,
        original_runes: this.original_runes,
        changed_runes: this.changed_runes,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = res.character
        this.local_character.runes = [...this.local_character.runes]
        //-------------------
        // calc integs total
        //-------------------
        this.local_character.pow_integs_tot = calcIntegsTotal({
          runes: this.local_character.runes,
        })
        this.changed_runes = res.changed_runes
      }
      console.log(
        '3. character skill_adjs AFTER adding rune: ',
        this.$deepCopy(this.local_character.skill_adjs),
      )
      //------------------------------------------------------
      // check if rune integration requires runecasting skill
      //------------------------------------------------------
      if (res.requireRuneCastingSkill) {
        console.log(
          '4. off to handle adding Runecasting skill requireRuneCastingSkill = ',
          res.requireRuneCastingSkill,
        )
        //---------------------------
        // manage runecasting change
        //---------------------------
        await this.manageRunecastingChange({ rune: res.new_rune })
      }
      console.log(
        '4b. after manageRunecastingChange res.new_rune = ',
        this.$deepCopy(res.new_rune),
      )
      //---------------------------------------------------
      // check if rune integration requires apply rune adjs
      //----------------------------------------------------
      if (res.requireApplyRuneAdjs) {
        console.log(
          '5. off to handle adding Runecasting skill requireApplyRuneAdjs = ',
          res.requireApplyRuneAdjs,
        )
        //--------------------------
        // manage rune adjs changes
        //--------------------------
        await this.manageRuneAdjChanges({
          new_casting_skill: res.requireRuneCastingSkill,
          rune: res.new_rune,
        })
      }
      this.recalculatePowAndMagicPoints()
      console.log('END handleAddRune!!!!!! new rune:', window.$deepCopy(res))
      console.log('END handleAddRune!!!!!! new rune:', res.new_rune.name)
    },
    //==============================================
    // handle rune adj item changes
    //------------------------------
    // called when character item un/linked to rune
    //  - update *all* adjs associated with this rune (rune-level adjs, integrated_rune arrays and
    //     character-level detail adjs) so their character_item matches the new rune link
    //----------------------------------------------
    handleRuneItemAdjChanges({ rune, item_id, change_value }) {
      console.log('--------------------------------')
      console.log('IN handleRuneItemAdjChanges')
      console.log('rune = ', rune)
      console.log('item_id (previous) = ', item_id)
      console.log('change_value (new item id) = ', change_value)

      // normalise rune reference from local_character in case
      // the incoming rune object is a stale copy
      const characterRune =
        this.local_character.runes.find((r) => r.id === rune.id) || rune
      const runeName = characterRune.name
      const newItemId = change_value ?? null

      let updateRequired = false

      const applyItemToAdj = (adj, context) => {
        // We treat all adjs for this rune as linked to the
        // rune's item; simply mirror the new item id (or null).
        if (adj) {
          console.log(
            'Updating adj character_item for context =',
            context,
            'adj = ',
            adj,
          )
          adj.character_item = newItemId
          updateRequired = true
        }
      }

      //------------------------------------------------
      // A. update rune-level SKILL / CHARACTERISTIC /
      //    ABILITY adjs for this rune
      //------------------------------------------------
      if (characterRune.skill_adjs && characterRune.skill_adjs.length > 0) {
        console.log('handling skill adj item change for rune.skill_adjs')
        characterRune.skill_adjs.forEach((adj) =>
          applyItemToAdj(adj, 'rune.skill_adjs'),
        )
      }

      // some code paths use characteristic_adjs, others
      // characteristics_adjs – update both if present
      const runeCharAdjs =
        characterRune.characteristic_adjs || characterRune.characteristics_adjs
      if (runeCharAdjs && runeCharAdjs.length > 0) {
        console.log(
          'handling characteristic adj item change for rune.characteristic(s)_adjs',
        )
        runeCharAdjs.forEach((adj) =>
          applyItemToAdj(adj, 'rune.characteristic(s)_adjs'),
        )
      }

      if (characterRune.ability_adjs && characterRune.ability_adjs.length > 0) {
        console.log('handling ability adj item change for rune.ability_adjs')
        characterRune.ability_adjs.forEach((adj) =>
          applyItemToAdj(adj, 'rune.ability_adjs'),
        )
      }

      //------------------------------------------------
      // B. update INTEGRATED-RUNE SKILL adjs on skills
      //------------------------------------------------
      if (Array.isArray(this.local_character.skills)) {
        this.local_character.skills.forEach((skill) => {
          if (Array.isArray(skill.integrated_rune_skill_adjs)) {
            skill.integrated_rune_skill_adjs.forEach((adj) => {
              if (
                adj &&
                (adj.rune_name === runeName || adj.rune === runeName)
              ) {
                applyItemToAdj(adj, 'skill.integrated_rune_skill_adjs')
              }
            })
          }
        })
      }

      //---------------------------------------------------------
      // C. update INTEGRATED-RUNE CHARACTERISTIC adjs on chars
      //---------------------------------------------------------
      if (Array.isArray(this.local_character.characteristics)) {
        this.local_character.characteristics.forEach((ch) => {
          if (Array.isArray(ch.integrated_rune_characteristic_adjs)) {
            ch.integrated_rune_characteristic_adjs.forEach((adj) => {
              if (
                adj &&
                (adj.rune_name === runeName || adj.rune === runeName)
              ) {
                applyItemToAdj(
                  adj,
                  'characteristic.integrated_rune_characteristic_adjs',
                )
              }
            })
          }
        })
      }

      //------------------------------------------------
      // D. update CHARACTER-LEVEL detail adj arrays for
      //    this rune (source === 'integrated_rune')
      //------------------------------------------------
      const updateDetailArray = (adjs, label) => {
        if (!Array.isArray(adjs)) return
        adjs.forEach((adj) => {
          if (!adj) return
          const isRuneMatch =
            adj.rune === runeName || adj.rune_name === runeName
          const isIntegratedRune = adj.source === 'integrated_rune'
          if (isRuneMatch && isIntegratedRune) {
            applyItemToAdj(adj, label)
          }
        })
      }

      updateDetailArray(this.local_character.skill_adjs, 'character.skill_adjs')
      updateDetailArray(
        this.local_character.characteristic_adjs,
        'character.characteristic_adjs',
      )
      updateDetailArray(
        this.local_character.ability_adjs,
        'character.ability_adjs',
      )

      //-------------------------
      // totals do not change – only
      // the item link, so no recalc
      //-------------------------
      if (updateRequired) {
        console.log(
          'Rune adjs updated with new character item link for rune = ',
          runeName,
          ' newItemId = ',
          newItemId,
        )
      }
    },
    //=============================================
    // manage runecasting change
    //---------------------------
    // called when:
    // a) adding new rune where integrated, OR
    // b) integrated set ON for existing rune
    //-----------------------------------------
    async manageRunecastingChange({ rune }) {
      console.log('--------------------------------')
      console.log('IN manageRunecastingChange')
      console.log('rune = ', rune)
      console.log('4. Adding Runecasting skill as required by integrated rune')

      const existingRunecastingSkill = getSpecificRuneCastingSkill({
        skills: this.local_character.skills,
        target_rune: rune,
      })

      if (existingRunecastingSkill) {
        console.log(
          '5. Skipping Runecasting add - existing rune-name skill found = ',
          existingRunecastingSkill,
        )
        return
      }

      //------------------
      // get static skill
      //------------------
      const casting_skill =
        this.$store.getters['characters/getSkill']('Runecasting')
      console.log('5. casting_skill = ', casting_skill)
      if (casting_skill) {
        console.log('6. Adding Runecasting skill to character')
        //----------------------------------
        // create new character skill entry
        //----------------------------------
        console.log('7. off to create new character skill entry')
        const add_parms = {
          name: casting_skill.name, // 'Runecasting'
          payload: {
            id: createTempId(),
            display_name: rune.name,
            character: this.local_character.id ? this.local_character.id : null,
            skill: casting_skill.id,
            ticks: 0,
            training: 0,
          },
        }
        console.log(
          '8. runecasting add_parms >>>>> =',
          this.$deepCopy(add_parms),
        )
        console.log(
          '8b. character skill_adjs BEFORE handleAddSkill: ',
          this.$deepCopy(this.local_character.skill_adjs),
        )
        console.log(
          '8c. character skills BEFORE handleAddSkill: ',
          this.$deepCopy(this.local_character.skills),
        )
        //------------------------------
        // handle add runecasting skill
        //------------------------------
        await this.handleAddSkill(add_parms)
        console.log(
          '8d. character skill_adjs AFTER handleAddSkill: ',
          this.$deepCopy(this.local_character.skill_adjs),
        )
        console.log(
          '8e. character skills AFTER handleAddSkill: ',
          this.$deepCopy(this.local_character.skills),
        )
      } else {
        console.error('9. Runecasting skill not found in store')
      }
    },
    //=============================================
    // manage sorcery casting change
    //-------------------------------
    // called when adding a new sorcery spell that
    // requires a dedicated Sorcery Casting skill
    //---------------------------------------------
    async manageSorceryCastingChange({ spell }) {
      console.log('--------------------------------')
      console.log('IN manageSorceryCastingChange')
      console.log('spell = ', spell)
      //------------------
      // get static Sorcery Casting skill
      //------------------
      const casting_skill =
        this.$store.getters['characters/getSkill']('Sorcery Casting')
      console.log('casting_skill (Sorcery Casting) = ', casting_skill)
      if (!casting_skill) {
        console.error('Sorcery Casting skill not found in store')
        return
      }

      //----------------------------------
      // create new character skill entry
      //----------------------------------
      const add_parms = {
        name: casting_skill.name, // 'Sorcery Casting'
        payload: {
          id: createTempId(),
          // tie this casting skill to the spell name so
          // trackSkillChanges can distinguish per-spell
          display_name: spell.name,
          character: this.local_character.id ? this.local_character.id : null,
          skill: casting_skill.id,
          ticks: 0,
          training: 0,
        },
      }
      console.log(
        'sorcery casting add_parms >>>>> =',
        this.$deepCopy(add_parms),
      )
      console.log(
        'character skills BEFORE sorcery casting handleAddSkill: ',
        this.$deepCopy(this.local_character.skills),
      )
      //------------------------------
      // handle add sorcery casting skill
      //------------------------------
      await this.handleAddSkill(add_parms)
      console.log(
        'character skills AFTER sorcery casting handleAddSkill: ',
        this.$deepCopy(this.local_character.skills),
      )
    },
    //=============================================
    // manage rune adj changes
    //-------------------------
    // called when:
    // a) adding new rune where integrated, OR
    // b) integrated set ON for existing rune, OR
    // c) removing/adding character item link
    //---------------------------------------------
    async manageRuneAdjChanges({ new_casting_skill = false, rune } = {}) {
      console.log('--------------------------------')
      console.log('IN manageRuneAdjChanges')
      console.log('rune = ', rune)
      console.log('1. Apply rune adjs for ACTIVE integrated rune')
      //--------------------------------------------
      // process apply rune adjs (for active runes) [i.e. apply the adjs for this rune to target skills/characteristics/abilities]
      //--------------------------------------------
      this.local_character = processApplyNewRuneAdjs({
        rune: rune,
        character: this.local_character,
      })
      console.log('2. local_character = ', this.$deepCopy(this.local_character))
      //----------------------------------------------------------------------------------------------------------------------------
      // process skill adjs [i.e. collect/load adjs for this skill] - skill adjs would be applied when adding the runecasting skill
      //----------------------------------------------------------------------------------------------------------------------------
      if (!new_casting_skill) {
        console.log('11a. new_casting_skill = ', new_casting_skill)
        //----------------------------
        // get casting skill for rune
        //----------------------------
        const casting_skill = getSpecificRuneCastingSkill({
          skills: this.local_character.skills,
          target_rune: rune,
        })
        //-------------------
        // got casting skill
        //-------------------
        if (casting_skill) {
          console.log('11b. casting_skill for rune = ', casting_skill)
          console.log(
            '12. off to processSkillAdjs for runecasting skill rune = ' +
              rune.name,
          )
          const res = processSkillAdjs({
            skill: casting_skill,
            character: this.local_character,
          })
          //----------------
          // load response
          //---------------
          if (res) {
            this.local_character = res.character
          } else {
            console.error(
              'Error processing skill adjs for new skill = ' +
                casting_skill.display_name,
            )
          }
          //-------------
          // catch error
          //-------------
        } else {
          console.error('No casting skill found for rune = ' + rune.name)
        }
      }

      this.recalculatePowAndMagicPoints()
    },
    getLinkedRuneSpellEntriesForRune(rune) {
      return getLinkedRuneSpellEntries({
        character: this.local_character,
        rune,
      })
    },
    getLinkedCharacterItemId(entry) {
      if (!entry) {
        return null
      }

      const rawLinkedItem =
        entry.character_item ?? entry.character_item_id ?? entry.item_id ?? null

      if (rawLinkedItem == null || rawLinkedItem === '') {
        return null
      }

      if (typeof rawLinkedItem === 'object') {
        return rawLinkedItem.id ?? null
      }

      return rawLinkedItem
    },
    isEntryLinkedToCharacterItem(entry, itemId) {
      const linkedItemId = this.getLinkedCharacterItemId(entry)

      if (linkedItemId == null || itemId == null) {
        return false
      }

      return String(linkedItemId) === String(itemId)
    },
    getRuneDeleteDisplayName(rune) {
      const displayName =
        typeof rune?.display_name === 'string' ? rune.display_name.trim() : ''
      const fallbackName =
        typeof rune?.name === 'string' ? rune.name.trim() : ''

      return displayName || fallbackName || ''
    },
    closeRunePanelIfOpen() {
      const runeSheet = this.$refs.SheetRunes

      if (runeSheet?.isSidePanelOpen) {
        runeSheet.handleRunePanelClose()
      }
    },
    //=============================================
    // handle delete rune
    //--------------------
    async handleDeleteRune(delete_rune, options = {}) {
      console.log('--------------------------------')
      console.log('IN handleDeleteRune')
      console.log('delete_rune = ', delete_rune)
      const { suppressConfirm = false } = options
      const runeDisplayName = this.getRuneDeleteDisplayName(delete_rune)
      /*
      -----------------------------------------------------------------------------------------------------------------
      SUMMARY:
      --------
      where rune integrated:
      - check other runes of same name still integrated
        - where none:
          - remove rune adjs from character
          - keep runecasting skill and let it become inactive/readonly
      - process delete rune (regardless of integration)

      -----------------------------------------------------------------------------------------------------------------
      */
      const linkedRuneSpellEntries =
        this.getLinkedRuneSpellEntriesForRune(delete_rune)

      if (!suppressConfirm && linkedRuneSpellEntries.length > 0) {
        const resp = await this.$refs.runeLinkedValuesDialog.show({
          title: 'Delete Character Rune',
          message: 'Confirm rune deletion and all linked values:',
          runeName: runeDisplayName,
          details: linkedRuneSpellEntries.map((entry) => entry.label),
          okButton: 'Delete',
          cancelButton: 'Cancel',
        })

        if (resp?.cancel) {
          return
        }
      } else if (!suppressConfirm) {
        const ok = await this.$refs.confirmDialog.show({
          title: 'Delete Character Rune',
          message: 'Confirm Deletion',
          subject: runeDisplayName,
          okButton: 'Delete',
          cancelButton: 'Cancel',
        })

        if (!ok) {
          return
        }
      }

      linkedRuneSpellEntries.forEach((entry) => {
        this.handleDeleteSpell(entry.spell)
      })

      //----------------------------
      // check for rune integration
      //----------------------------
      if (delete_rune.integrated) {
        console.log('rune integrated...')
        //--------------------------------------------------
        // get cnt of runes with name same still integrated
        //--------------------------------------------------
        const integrated_rune_cnt = getSpecificRuneIntegratedCount({
          runes: this.local_character.runes,
          target_rune: delete_rune,
        })
        console.log('integrated_rune_cnt = ', integrated_rune_cnt)
        //-----------------------------------------
        // where cnt ONE - no same rune integrated
        //-----------------------------------------
        if (integrated_rune_cnt == 1) {
          console.log('no same rune integrated')
          //------------------
          // remove rune adjs
          //------------------
          this.local_character = processRemoveRuneAdjs({
            rune_name: delete_rune.name,
            character: this.local_character,
          })
        }
      } else {
        console.log(`rune ${delete_rune.name} NOT integrated...`)
      }

      console.log('off to delete rune = ', runeDisplayName)
      //--------------------------------
      // process delete rune regardless
      //--------------------------------
      const res = processDeleteRune({
        rune: delete_rune,
        original_runes: this.original_runes,
        changed_runes: this.changed_runes,
        character: this.local_character,
      })
      //---------------
      // load response
      //---------------
      if (res) {
        this.local_character = res.character
        this.local_character.runes = [...res.character.runes]
        this.local_character.pow_integs_tot = calcIntegsTotal({
          runes: this.local_character.runes,
        })
        this.changed_runes = res.updated_changed_runes
        this.closeRunePanelIfOpen()
        this.recalculatePowAndMagicPoints()
      }
      console.log('END!!! handleDeleteRune for rune:', runeDisplayName)
    },
    //=============================================
    // handle delete skill
    //---------------------
    async handleDeleteSkill(skill) {
      console.log('--------------------------------')
      console.log('IN handleDeleteSkill skill = ', skill)
      console.log('qqqq skill adjs = ', this.local_character.skill_adjs)

      const dependentSkills = findDependentCharacterSkills(
        this.local_character?.skills ?? [],
        skill,
      )

      if (dependentSkills.length > 0) {
        const dependentNames = dependentSkills
          .map((entry) => entry.name)
          .join(', ')
        const ok = await this.$refs.confirmDialog.show({
          title: 'Delete Character Skill',
          message:
            'Deleting this parent skill will also delete dependent skills.',
          subject: dependentNames,
          okButton: 'Delete',
          cancelButton: 'Cancel',
        })

        if (!ok) {
          return
        }
      }

      const skillsToDelete = [...dependentSkills, skill]

      skillsToDelete.forEach((entry) => {
        const res = processDeleteSkill({
          skill: entry,
          original_skills: this.original_skills,
          changed_skills: this.changed_skills,
          character: this.local_character,
        })

        console.log('rrrr skill adjs = ', this.local_character.skill_adjs)

        if (!res) {
          console.error('NO SKILL DELETED')
          return
        }

        this.local_character = res.character
        console.log('ssss skill adjs = ', this.local_character.skill_adjs)
        this.changed_skills = res.updated_changed_skills
      })

      this.local_character = {
        ...this.local_character,
        skills: this.local_character.skills,
      }
    },
    //=============================================
    // handle delete count
    //---------------------
    handleDeleteCount(count) {
      console.log('--------------------------------')
      console.log('IN handleDeleteCount count = ', count)
      //-------------------------
      // go process delete count
      //-------------------------
      let res = processDeleteCount({
        count: count,
        original_counts: this.original_counts,
        changed_counts: this.changed_counts,
        character: this.local_character,
      })
      console.log('counts after deletion = ', this.local_character.counts)
      //-----------------
      // handle response
      //-----------------
      if (res) {
        this.local_character = res.character
        this.changed_counts = res.updated_changed_counts
        //-----------------------------------------------
        // replace counts with itself (force reactivity)
        //-----------------------------------------------
        this.local_character = {
          ...this.local_character,
          counts: this.local_character.counts,
        }
      } else {
        console.error('NO COUNT DELETED')
      }
    },
    //=============================================
    // handle delete campaign note
    //-----------------------------
    handleDeleteCampaignNote(campaign_note) {
      console.log('--------------------------------')
      console.log('IN handleDeleteCampaignNote note = ', campaign_note)
      //------------------------
      // go process delete note
      //------------------------
      let res = processDeleteCampaignNote({
        campaign_note: campaign_note,
        original_campaign_notes: this.original_campaign_notes,
        changed_campaign_notes: this.changed_campaign_notes,
        character: this.local_character,
      })
      //-----------------
      // handle response
      //-----------------
      if (res) {
        this.local_character = res.character
        this.changed_campaign_notes = res.updated_changed_campaign_notes
        //-------------------------------------------------------
        // replace campaign_notes with itself (force reactivity)
        //-------------------------------------------------------
        this.local_character = {
          ...this.local_character,
          campaign_notes: this.local_character.campaign_notes,
        }
      } else {
        console.error('NO NOTE DELETED')
      }
    },
    //=============================================
    // handle delete item
    //--------------------
    async handleDeleteItem(deleteRequest) {
      console.log('--------------------------------')
      console.log('IN handleDeleteItem deleteRequest = ', deleteRequest)
      const item = deleteRequest?.item ?? deleteRequest
      const delete_payload = deleteRequest?.delete_payload ?? null

      if (!item) {
        console.error('NO ITEM PROVIDED FOR DELETE')
        return
      }
      //------------------------------------------------------------------
      // BEFORE ITEM DELETION: first clear all values linked to this item
      // (runes, skill/ability/characteristic adjs, locations, etc.)
      //------------------------------------------------------------------
      this.handleClearItem({ item_id: item.id })
      console.log('items before deletion = ', this.local_character.items)

      //-------------------------------------------------------------------
      // BEFORE ITEM DELETION: check equipped armour status/flag if armour
      //-------------------------------------------------------------------
      let remove_equipped_armour = item.equipped ? item.id : null
      //---------------------------------
      // handle removing equipped armour
      //---------------------------------
      if (remove_equipped_armour) {
        console.log('remove_equipped_armour = ', remove_equipped_armour)
        this.handleRemoveEquippedArmour({ armour_id: remove_equipped_armour })
      }

      //-------------------------
      // go process delete item
      //-------------------------
      let res = processDeleteItem({
        item: item,
        original_items: this.original_items,
        changed_items: this.changed_items,
        character: this.local_character,
        delete_payload,
      })

      //-----------------
      // handle response
      //-----------------
      if (res) {
        this.local_character = res.character
        this.changed_items = res.updated_changed_items
        //----------------------------------------------------
        // process any tagged containers (for contents calcs)
        //----------------------------------------------------
        if (res.calc_containers && res.calc_containers.length > 0) {
          console.log('container changes to apply = ', res.calc_containers)
          //--------------------
          // loop container ids
          //--------------------
          res.calc_containers.forEach((container_id) => {
            //-------------------------------------------------------------------------------
            // call container contents calc handler (will trigger watch to update character)
            //-------------------------------------------------------------------------------
            this.handleCalcContainerContents({ container_id })
          })
        }

        //-----------------------------------------------
        // replace items with itself (force reactivity)
        //-----------------------------------------------
        this.refreshCharacterEncState()

        await this.syncCharacterOverloadingState({
          showTransitionPopup: true,
        })
        this.refreshCharacterEncState()
      } else {
        console.error('NO ITEM DELETED')
      }
    },
    //=============================================
    // handle close adjs
    //------------------
    handleCloseAdjs() {
      this.isAdjsViewOpen = false
      this.isOptAdjsViewOpen = false
      this.adj_category = ''
      this.adj_type = ''
      this.min_adj = 0
      this.max_adj = 0
      //----------
      // set view
      //----------
      this.setView('character_sheets')
    },
    //=============================================
    // handle calc totals rebuild reason adjs (NB: adjs packaged up for db updated in processSubmission)
    //----------------------------------------
    handleCalcTotalsRebuildReasonAppliedAdjs(payload) {
      console.log('--------------------------------')
      console.log(
        'IN handleCalcTotalsRebuildReasonAppliedAdjs in CharacterSheetView - payload = ',
        payload,
      )
      console.log(
        'IN handleCalcTotalsRebuildReasonAppliedAdjs in CharacterSheetView - character = ',
        this.local_character,
      )
      if (payload.category === 'characteristic-skill') {
        const updatedAdjs = normalizeCharacteristicSkillAdjs(payload.data).map(
          (adj) => ({
            ...adj,
            name: payload.type,
            characteristic_name: payload.type,
          }),
        )

        const retainedAdjs = (
          this.local_character.characteristic_skill_adjs || []
        ).filter(
          (adj) => (adj.name || adj.characteristic_name) !== payload.type,
        )

        this.local_character.characteristic_skill_adjs = [
          ...retainedAdjs,
          ...updatedAdjs,
        ]
        this.local_character = loadSkillAdjsHarness(this.local_character)
        this.local_character = processCalcTotalsRebuildReasonAppliedAdjsHarness(
          {
            adjs: this.local_character.skill_adjs,
            category: 'skill',
            character: this.local_character,
            add_only: true,
          },
        )
        this.local_character = copyObjectNoRef(this.local_character)
        this.handleCloseAdjs()
        return
      }
      //-----------------------------------------
      // get updated character after savings adjs
      //------------------------------------------
      this.local_character = processCalcTotalsRebuildReasonAppliedAdjs({
        payload: payload,
        character: this.local_character,
      })
      this.local_character = { ...this.local_character }
      console.log(
        'OUT handleCalcTotalsRebuildReasonAppliedAdjs in CharacterSheetView - updated character = ',
        this.local_character,
      )
      //-------------------
      // clean-up on close
      //-------------------
      this.handleCloseAdjs()
    },
    //=============================================
    // handle calc count rebuild reason opt adjs
    //-------------------------------------------
    handleCalcCountRebuildReasonOptAdjs(payload) {
      console.log('--------------------------------')
      console.log(
        'IN handleCalcCountRebuildReasonOptAdjs in CharacterSheetView - payload = ',
        payload,
      )
      console.log(
        'IN handleCalcCountRebuildReasonOptAdjs in CharacterSheetView - character = ',
        this.local_character,
      )
      //----------------------------------------------------------------------------------------
      // get updated character after recalc adj count and rebuild reasons string for this skill
      //----------------------------------------------------------------------------------------
      this.local_character = processCalcCountRebuildReasonOptAdjs({
        payload: payload,
        character: this.local_character,
      })
      this.local_character = { ...this.local_character }
      console.log(
        'OUT handleCalcCountRebuildReasonOptAdjs in CharacterSheetView - updated character = ',
        this.local_character,
      )
      //-------------------
      // clean-up on close
      //-------------------
      this.handleCloseAdjs()
    },
    //=============================================
    // handle character save
    //-----------------------
    async handleCharacterSave() {
      console.log('---------------------------')
      console.log('IN handleCharacterSave')
      this.errorMsgs.splice(0)
      this.errorTabs.splice(0)
      this.errorFields.splice(0)
      //-----------------
      // capitalise name
      //-----------------
      this.local_character.name = capitaliseFirstLetter(
        this.local_character.name,
      )
      //------------------------------
      // validate character (profile)
      //------------------------------
      let error = validateCharacterProfile(
        this.local_character,
        this.original_character,
      )
      //----------------------------
      // if error - force tab click
      //----------------------------
      if (error.msg != '') {
        this.errorMsgs.push(window.bbUiErrorMsg(error.msg))
        this.errorFields.push(error.field)
        this.errorTabs.push(error.tab)
        //
        document.getElementById(this.errorTabs[0]).click()
      } else {
        console.log('XXX CHANGED_RUNES = ', this.changed_runes)
        //----------------------------------------
        // remove any tmp ids from changed arrays
        //----------------------------------------
        stripArrayTempIds(this.changed_runes)
        stripArrayTempIds(this.changed_skills)
        stripArrayTempIds(this.changed_items)
        stripArrayTempIds(this.changed_counts)
        stripArrayTempIds(this.changed_spells)
        stripArrayTempIds(this.changed_campaign_notes)

        console.log('CHANGED_CAMPAIGN_NOTES AFTER STRIP TMP = ', this.changed_campaign_notes)
        //-----------------------------------------------------------------------------
        // remove add runecasting skill (auto-created in backend for integrated runes)
        //-----------------------------------------------------------------------------
        console.log(
          'CHANGED SKILLS BEFORE STRIP TMP - RUNES = ',
          this.changed_skills,
        )
        //---------------------------------------------------------
        // convert add runecasting & sorcery casting skill actions
        //---------------------------------------------------------
        this.changed_skills = convertAddRunecastingSkillActions(
          this.changed_skills,
        )
        this.changed_skills = convertAddSorceryCastingSkillActions(
          this.changed_skills,
          this.changed_spells,
        )

        const deletedSkillNames = this.changed_skills
          .filter((entry) => entry?.action === 'DELETE')
          .map((entry) => entry?.name)
          .filter(Boolean)

        const dependencyError = this.changed_skills
          .filter((entry) => entry?.action === 'ADD')
          .map((entry) =>
            getMissingParentSkillMessage({
              characterName: this.local_character?.name ?? '',
              staticSkill: this.$store.getters['characters/getStaticSkill'](
                entry?.payload?.skill,
              ),
              skills: this.local_character?.skills ?? [],
              deletedSkillNames,
            }),
          )
          .find(Boolean)

        if (dependencyError) {
          await this.$refs.warningDialog.show({
            title: 'Save Character Skills',
            message: dependencyError,
            subject: '',
            okButton: 'OK',
          })
          return
        }

        const resolveFavouriteSkillMeta = (skill) => {
          const getStaticSkill =
            this.$store?.getters?.['characters/getStaticSkill']
          const getStaticSkillByName =
            this.$store?.getters?.['characters/getStaticSkillByName']

          if (skill?.skill && typeof getStaticSkill === 'function') {
            const byId = getStaticSkill(skill.skill)
            if (byId) {
              return byId
            }
          }

          if (typeof getStaticSkillByName === 'function') {
            return (
              getStaticSkillByName(skill?.display_name) ||
              getStaticSkillByName(skill?.name) ||
              null
            )
          }

          return null
        }

        const projectedSkills = projectSkillsWithChangedFavourites(
          this.local_character?.skills ?? [],
          this.changed_skills,
          resolveFavouriteSkillMeta,
        )

        const favouriteOverflow = getFavouriteSkillBucketOverflow(
          projectedSkills,
          typeof this.getConfig === 'function'
            ? this.getConfig()
            : this.getConfig,
          resolveFavouriteSkillMeta,
        )

        if (favouriteOverflow) {
          await this.$refs.warningDialog.show({
            title: 'Save Character Skills',
            message:
              `Too many ${favouriteOverflow.label} marked as favourite. ` +
              `Max ${favouriteOverflow.limit} allowed, currently ${favouriteOverflow.count}.`,
            subject: 'Remove an existing favourite and save again.',
            okButton: 'OK',
          })
          return
        }

        console.log(
          'CHANGED SKILLS AFTER STRIP TMP - RUNES = ',
          this.changed_skills,
        )
        //-------------------------------------------------------------
        // new-character drafts default combat_inclusion to true during
        // initial setup, so make sure the profile payload carries that
        // default even when it was not tracked into changed_profile.
        //-------------------------------------------------------------
        if (
          this.listScope === 'mine' &&
          this.isNewCharacter &&
          this.local_character?.combat_inclusion === true
        ) {
          if (
            !this.changed_profile ||
            typeof this.changed_profile !== 'object'
          ) {
            this.changed_profile = {}
          }

          if (
            !this.changed_profile.payload ||
            typeof this.changed_profile.payload !== 'object'
          ) {
            this.changed_profile.payload = {}
          }

          if (
            !Object.prototype.hasOwnProperty.call(
              this.changed_profile.payload,
              'combat_inclusion',
            )
          ) {
            this.changed_profile.payload.combat_inclusion = true
          }
        }
        //--------------------
        // set changes object
        //--------------------
        const changes = {
          profile: this.changed_profile,
          chars: this.changed_chars,
          skills: this.changed_skills,
          runes: this.changed_runes,
          items: this.changed_items,
          counts: this.changed_counts,
          spells: this.changed_spells,
          armour_locations: this.changed_armour_locations,
          campaign_notes: this.changed_campaign_notes,
        }
        console.log('CHANGES TO PROCESS = ', changes)
        //-----------------
        // process changes
        //-----------------
        /*
        - tracked changes passed to processSubmission
        - adj changes detected in this routine
        */
        const change_parms = processSubmission({
          changes,
          original_adjs: this.original_adjs,
          character: this.local_character,
        })
        if (this.isNewCharacter) {
          change_parms.initial_save_mp_cur =
            Number(this.local_character?.mp_cur) || 0
        }
        console.log('CHANGE PARMS TO SAVE = ', change_parms)
        //---------------
        // where changes
        //---------------
        if (change_parms) {
          console.log(
            'CHANGES DETECTED TO SAVE FOR CHARACTER = ' +
              this.local_character.name,
          )
          console.log('CHARACTER ID = ' + this.local_character.id)
          //---------------------------------
          // emit save character sheet event
          //---------------------------------
          this.$emit('save-character-sheet', change_parms)
        } else {
          //------------------
          // catch no changes
          //------------------
          console.log(
            'NO CHANGES TO SAVE FOR CHARACTER = ' + this.local_character.name,
          )
        }
      }
    },
    //=============================================
    // handle toggle table details
    //-----------------------------
    handleToggleTableDetails() {
      console.log('IN handleToggleTableDetails in CharacterSheetView')
      this.toggleDetails = !this.toggleDetails

      if (this.activeTab.startsWith('items')) {
        setTimeout(() => {
          this.refreshItemsHorizontalScrollState()
        }, 300)
      }

      setTimeout(() => {
        this.refreshVerticalScrollState()
      }, 300)
    },
    //=============================================
    // handle scroll table
    //---------------------
    handleScrollTable(dir) {
      // console.log('IN handleScrollTable in CharacterSheetView - dir = ' + dir)
      // console.log('this.activeTab = ' + this.activeTab)
      //-----------------------
      // scroll table (skills)
      //-----------------------
      if (this.activeTab.startsWith('skills')) {
        callSheetMethod(this, 'SheetSkills', 'handleScrollTable', dir)

        if (dir === 'up' || dir === 'down') {
          setTimeout(() => {
            this.refreshVerticalScrollState()
          }, 320)
        }
        //----------------------
        // scroll table (items)
        //----------------------
      } else if (this.activeTab.startsWith('items')) {
        callSheetMethod(this, 'SheetItems', 'handleScrollTable', dir)

        if (dir === 'left' || dir === 'right') {
          this.itemsLastHorizontalDir = dir
          this.itemsNextHorizontalDir = dir

          setTimeout(() => {
            this.refreshItemsHorizontalScrollState()
          }, 320)
        }

        if (dir === 'up' || dir === 'down') {
          setTimeout(() => {
            this.refreshVerticalScrollState()
          }, 320)
        }
        //----------------------
        // scroll table (runes)
        //----------------------
      } else if (this.activeTab === 'runes') {
        callSheetMethod(this, 'SheetRunes', 'handleScrollTable', dir)

        if (dir === 'up' || dir === 'down') {
          setTimeout(() => {
            this.refreshVerticalScrollState()
          }, 320)
        }
        //-------------
        // catch error
        //-------------
      } else {
        console.warn('Cannot scroll table - unknown active tab')
      }
    },
    refreshVerticalScrollState() {
      if (this.activeTab.startsWith('items-')) {
        const state = callSheetMethod(
          this,
          'SheetItems',
          'getVerticalScrollState',
        )
        if (state && typeof state === 'object') {
          this.canScrollDown = !!state.canScrollDown
          this.canScrollUp = !!state.canScrollUp
          return
        }
      }

      const usesBoardScroll =
        this.activeTab.startsWith('skills') ||
        this.activeTab === 'runes' ||
        this.activeTab.startsWith('spells-')

      if (!usesBoardScroll) {
        this.canScrollDown = false
        this.canScrollUp = false
        return
      }

      const el = document.getElementById('board')
      if (!el) {
        this.canScrollDown = false
        this.canScrollUp = false
        return
      }

      const epsilon = 2
      this.canScrollDown =
        el.scrollTop < el.scrollHeight - el.clientHeight - epsilon
      this.canScrollUp = el.scrollTop > epsilon
    },
    refreshItemsHorizontalScrollState() {
      const state = callSheetMethod(
        this,
        'SheetItems',
        'getHorizontalScrollState',
      )

      if (!state || typeof state !== 'object') {
        this.itemsCanScrollLeft = false
        this.itemsCanScrollRight = true
        this.itemsNextHorizontalDir = 'right'
        return
      }

      this.itemsCanScrollLeft = !!state.canScrollLeft
      this.itemsCanScrollRight = !!state.canScrollRight

      const preferredDir =
        this.itemsLastHorizontalDir === 'left' ? 'left' : 'right'

      if (preferredDir === 'right' && this.itemsCanScrollRight) {
        this.itemsNextHorizontalDir = 'right'
        return
      }

      if (preferredDir === 'left' && this.itemsCanScrollLeft) {
        this.itemsNextHorizontalDir = 'left'
        return
      }

      if (this.itemsCanScrollRight) {
        this.itemsNextHorizontalDir = 'right'
      } else if (this.itemsCanScrollLeft) {
        this.itemsNextHorizontalDir = 'left'
      } else {
        this.itemsNextHorizontalDir = 'right'
      }
    },
    //=============================================
    // handle character delete
    //-------------------------
    handleCharacterDelete(character_idx) {
      this.resetCurrentCharacter()
      this.$emit('delete-character-sheet', character_idx)
    },
    //=============================================
    // handle sheet export
    //---------------------
    async handleSheetExport() {
      console.log('IN handleSheetExport in CharacterSheetView')

      const characterSlug = this.local_character?.slug

      if (isEmpty(characterSlug)) {
        await this.$refs.warningDialog.show({
          title: 'WARNING',
          message: 'Save the character before exporting the sheet to PDF.',
          okButton: 'OK',
        })
        return
      }

      const apiBaseUrl = import.meta.env.VITE_API_URL || window.location.origin
      const exportUrl = new URL(
        `/api/v1/characters/${characterSlug}/sheet/?output=pdf`,
        apiBaseUrl,
      ).toString()
      const exportWindow = window.open('', '_blank')

      if (exportWindow) {
        exportWindow.opener = null
        exportWindow.location = exportUrl
        return
      }

      const exportLink = document.createElement('a')
      exportLink.href = exportUrl
      exportLink.target = '_blank'
      exportLink.rel = 'noopener noreferrer'
      document.body.appendChild(exportLink)
      exportLink.click()
      document.body.removeChild(exportLink)

      await this.$refs.warningDialog.show({
        title: 'WARNING',
        message: 'Allow popups for this site to open the exported PDF.',
        okButton: 'OK',
      })
    },
    //=============================================
    // handle sheet close
    //--------------------
    handleSheetClose() {
      console.log('IN handleSheetClose in CharacterSheetView')
      this.resetCurrentCharacter()
      this.$emit('close-character-sheet')
    },
    //=============================================
    // handle open adjs
    //------------------
    handleOpenAdjs(data) {
      console.log('IN handleOpenAdjs in CharacterSheetView - data = ', data)
      this.adj_category = data.adj_category
      this.adj_type = data.adj_type
      this.adj_label = data.adj_label
      this.isAdjsViewOpen = true

      if (
        this.adj_category == 'characteristic' ||
        this.adj_category == 'ability'
      ) {
        //---------------------------------------------------------------
        // set min/max adj from config for count (non-percent) type adjs
        //---------------------------------------------------------------
        this.min_adj = this.config.min_adj_count
        this.max_adj = this.config.max_adj_count
      } else {
        //---------------------------------------------------
        // set min/max adj from config for percent type adjs
        //---------------------------------------------------
        this.min_adj = this.config.min_adj_percent
        this.max_adj = this.config.max_adj_percent
      }
    },
    //=============================================
    // handle open optional adjs
    //---------------------------
    handleOpenOptAdjs(data) {
      console.log('IN handleOpenOptAdjs in CharacterSheetView - data = ', data)
      this.adj_category = data.adj_category
      this.adj_type = data.adj_type
      this.adj_label = data.adj_label
      this.isOptAdjsViewOpen = true
      //---------------------------------------------------
      // set min/max adj from config for percent type adjs
      //---------------------------------------------------
      this.min_adj = this.config.min_adj_percent
      this.max_adj = this.config.max_adj_percent
    },
    //=============================================
    // handle add character object
    //-----------------------------
    handleAddCharacterObject() {
      // console.log('IN handleAddCharacterObject in CharacterSheetView')
      // console.log('this.activeTab = ' + this.activeTab)
      //------------------------------
      // show skill panel (add skill)
      //------------------------------
      if (this.activeTab.startsWith('skills')) {
        callSheetMethod(this, 'SheetSkills', 'handleShowSkillPanel', null)
        //----------------------------
        // show rune panel (add rune)
        //----------------------------
      } else if (this.activeTab === 'runes') {
        callSheetMethod(this, 'SheetRunes', 'handleShowRunePanel', null)
        //----------------------------
        // show item panel (add item)
        //----------------------------
      } else if (this.activeTab.startsWith('items-')) {
        callSheetMethod(this, 'SheetItems', 'handleShowItemPanel', null)
        //---------------------------
        // add count row (add count)
        //---------------------------
      } else if (this.activeTab === 'counts') {
        callSheetMethod(this, 'SheetCounts', 'handleAddCountRow', null)
        //---------------------------
        // add spell row (add spell)
        //---------------------------
      } else if (this.activeTab.startsWith('spells-')) {
        callSheetMethod(this, 'SheetSpells', 'handleShowSpellPanel', null)
        //----------------------------------
        // add campaign note row (add note)
        //----------------------------------
      } else if (this.activeTab === 'campaign-notes') {
        callSheetMethod(this, 'SheetCampaignNotes', 'handleEditNote', null)
        //-------------
        // catch error
        //-------------
      } else {
        console.warn('Cannot handle add character object - unknown active tab')
      }
    },
    //========================================================
    // clear error field
    //------------------
    clearErrorField(fieldName) {
      const index = this.errorFields.indexOf(fieldName)
      if (index > -1) {
        this.errorFields.splice(index, 1)
        this.errorMsgs.splice(index, 1)
      }
    },
  },
  //========================================================
  // mounted
  //---------
  async mounted() {
    console.log('===============================')
    console.log('IN mounted CharacterSheetView')
    console.log('===============================')
    try {
      //----------
      // set view
      //----------
      this.setView('character_sheets')
      //----------------------
      // ensure combat config
      //----------------------
      if (!this.getConfig()) {
        await this.loadCombatConfig()
      }
      //-------------------
      // get config values
      //-------------------
      this.getConfigValues()
      //------------------------------------------------
      // wait for load calls to complete  (static data)
      //------------------------------------------------
      await Promise.all([
        //--------------------
        // load static skills
        //--------------------
        this.loadStaticSkills(),
        //--------------------
        // load static traits
        //--------------------
        this.loadStaticTraits(),
        //--------------------
        // load static runes
        //--------------------
        this.loadStaticRunes(),
        //--------------------
        // load static spells
        //--------------------
        this.loadStaticSpells(),
        //-----------------------
        // load static abilities
        //-----------------------
        this.loadStaticAbilities(),
        //-------------------
        // load static items
        //-------------------
        this.loadStaticItems(),
        //----------------------------
        // load static starter counts
        //----------------------------
        this.loadStaticStarterCounts(),
        //------------------------
        // load static currencies
        //------------------------
        this.loadStaticCurrencies(),
        //----------------------------------
        // load character lookups (choices)
        //----------------------------------
        this.loadCharacterLookups(),
        //----------------------------
        // load item lookups (choices)
        //----------------------------
        this.loadItemLookups(),
        //----------------------------
        // load artefact type lookups
        //----------------------------
        this.loadArtefactTypes(),
      ])
      //-----------------------
      // load character values
      //-----------------------
      if (this.character != null) {
        console.log('LOADING existing char values...')
        this.loadCharacter(this.character)
      } else {
        //----------------------------
        // setup new character values
        //----------------------------
        console.log('SETTING new char values...')
        this.setCharacter()
        this.isNewCharacterLoad = true
      }
      console.log('ZZZ local_character = ', this.local_character)
      //---------------------------------------
      // set current character for page header
      //---------------------------------------
      this.setCurrentCharacter(this.local_character)
      //---------------------
      // save original values
      //----------------------
      const originalValues = processSaveOriginalValues({
        character: this.local_character,
      })
      //---------------------
      // set original values
      //---------------------
      this.original_character = originalValues.character
      this.original_profile = originalValues.profile
      this.original_chars = originalValues.chars
      this.original_skills = originalValues.skills
      this.original_items = originalValues.items
      this.original_runes = originalValues.runes
      this.original_counts = originalValues.counts
      this.original_adjs = originalValues.adjs
      this.original_spells = originalValues.spells
      this.original_armour_locations = originalValues.armour_locations
      this.original_campaign_notes = originalValues.campaign_notes

      console.log('ORIGINAL campaign notes = ', this.original_campaign_notes)
      //--------------------
      // new character only
      //--------------------
      if (this.isNewCharacter) {
        //---------------------
        // set profile changes
        //---------------------
        this.changed_profile = setProfileChanges(this.original_profile)
        //------------------------------------------------------------------
        // NB: no need to set characteristics here as they will be created
        // in db following character creation. any changes from the race
        // defaults will be detected by track chars changes method. for new
        // characters these changes will be applied once the initial
        // characteristic records have been created in the backend.
        //------------------------------------------------------------------
        // this.changed_chars = setCharacteristicChanges(this.original_chars)
        //--------------------------------------------------------
        // go manage characteristic force changes (initial calcs)
        //--------------------------------------------------------
        getCharacteristicPrefixes().forEach((pfx) => {
          processCharacteristicChange({
            pfx,
            original_chars: this.original_chars,
            changed_chars: this.changed_chars,
            isNewCharacterLoad: this.isNewCharacterLoad,
            character: this.local_character,
          })
        })
        //--------------------------
        // switch off new char load
        //--------------------------
        this.isNewCharacterLoad = false
        this.refreshCharacterEncState()
      }
      //--------------------
      // turn on components
      //--------------------
      this.isReady = true
      //-----------------------------------------
      // initial vertical scroll state check
      //-----------------------------------------
      nextTick(() => {
        setTimeout(() => {
          this.refreshVerticalScrollState()
        }, 400)
      })
      //-----------------------
      // monitor tab selection
      //-----------------------
      nextTick(() => {
        const tabList = document.querySelectorAll(
          '#pills-tab [data-bs-toggle="pill"]',
        )
        tabList.forEach((tab) => {
          tab.addEventListener('shown.bs.tab', (event) => {
            let tabTarget = event.target.getAttribute('data-bs-target')
            if (!tabTarget) {
              tabTarget = event.target.getAttribute('href')
            }
            this.activeTab = getSubstringAfterFindString(tabTarget, '#pills-')
            let match = tabTarget.match(/^#pills-(.+)$/)
            this.activeTab = match ? match[1] : tabTarget
            console.log('Active tab changed to:', this.activeTab)

            if (this.activeTab.startsWith('items')) {
              setTimeout(() => {
                this.refreshItemsHorizontalScrollState()
              }, 300)
            }

            setTimeout(() => {
              this.refreshVerticalScrollState()
            }, 300)
          })
        })
      })
      //-----------------------
      // check over-encumbered
      //-----------------------
      await this.syncCharacterOverloadingState({
        showInitialPopup: true,
      })
      await this.syncHiddenRollSocketConnection()
    } catch (error) {
      console.error('CharacterSheetView mounted failed', error)
      const errorMessage =
        window.bbErrorMsg(error) ||
        error?.errorMsg ||
        'An unexpected error has occured.\nPlease contact the administrator.'

      this.errorMsgs.splice(0)
      this.errorFields.splice(0)
      this.errorMsgs.push(window.bbUiErrorMsg(errorMessage))
      this.errorFields.push('general')
    }

    this.$emit('child-ready')
  },
  beforeUnmount() {
    this.teardownArenaSocketConnection()
  },
}
</script>

<style scoped>
.tab-pane.bb-static-tab-pane.active {
  display: flow-root;
}

.bb-profile-only-tabs .nav-item:not(:first-child) {
  display: none;
}
</style>
